// src/views/admin/LiveGrid.js
// ============================================================================
// COMMAND CENTER: LIVE GRID — Enterprise-Grade Geospatial Asset Tracker
// ============================================================================
// Features:
//   - Custom SVG map markers per asset type (pump, sensor, tank, valve, pipeline)
//   - Real-time WebSocket integration (status sync, telemetry pulse, live PSI)
//   - AI Lens mode: health-score-based marker coloring + predictive overlays
//   - Real-time telemetry side panel with live status cards
//   - Asset search & multi-filter (type, status, zone)
//   - Map layer switcher (Voyager / Satellite / Dark / Terrain)
//   - Auto-refresh with configurable interval
//   - Fullscreen toggle for the map pane
//   - Asset detail flyout on marker click
//   - Phase 4: Asset Command Drawer (AI Diagnostics + Telemetry Chart + Action Hub)
//   - Smart UI Filtering: PSI/Telemetry section hidden for non-sensor assets
//   - One-Click Work Order Dispatch: Ollama brief + direct POST, duplicate prevention via DB check
//   - Ollama AI Triage: AI-generated technical brief on Work Order creation
//   - Phase 5: Geospatial Zone Overlay — GeoJSON district boundaries on the map
//   - Active Work Order Status Block: live ticket display + "View Details" link in drawer
//   - Responsive: stacked on mobile, side-by-side on desktop
//   - Glass-morphism command panels with Framer Motion stagger animations
//
// --- UPDATE (FlowSense ecosystem pass) ---------------------------------------
//   - Basemaps: CARTO via the FlowSense API key (Dark Matter, Voyager, Positron,
//     no-label variants) + Satellite, Hybrid, Esri Streets, Terrain, OSM.
//     Choice is remembered; tile load/error tracking with automatic fallback to
//     the public CARTO endpoint if the keyed endpoint fails (liveGridTiles.js).
//   - WebSocket: subscribes to the REAL backend events (asset_prediction_update,
//     anomaly_recorded, critical_anomaly_detected, work_order_events, asset /
//     utility updates, readings) through the 1-second flush engine, plus the
//     legacy window 'ws_message' events. Debounced refetch on asset/prediction
//     events; cross-page bus on work order creation.
//   - ML: "assets at risk" model merged into every asset (risk score, finding,
//     repair-vs-replace prescription); ML-risk rings and live-anomaly pulses.
//   - FlowSense AI: Grid AI strip on the map + Grid AI drawer (explainable grid
//     score, ranked priorities, Ask AI → Ollama with rules fallback, live feed,
//     ecosystem links) and a FlowSense AI insight section in the asset drawer.
//   - Work orders: typed predictive/corrective drafts (domain + work_order_type),
//     "Open in form", correct deep link to the work order, cross-page refresh.
//   - Fix: popup work-order lookup re-ran on every live patch (now per asset id).
// --- END UPDATE ---------------------------------------------------------------
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Center,
  Flex,
  Heading,
  Text,
  useColorModeValue,
  Spinner,
  HStack,
  VStack,
  Badge,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Tooltip,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  useToast,
  Collapse,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  Progress,
  SimpleGrid,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Stat,
  StatLabel,
  StatNumber,
  // Phase 4: Drawer imports
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  // Maintenance History Modal imports
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  // --- UPDATE: grouped basemap menu + colour-mode aware default basemap
  MenuGroup,
  MenuDivider,
  useColorMode,
  // --- END UPDATE
} from '@chakra-ui/react';
import {
  SearchIcon,
  CloseIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ViewIcon,
  SettingsIcon,
} from '@chakra-ui/icons';
import {
  FaExpand,
  FaCompress,
  FaLayerGroup,
  FaSatelliteDish,
  FaFilter,
  FaMapMarkerAlt,
  FaWater,
  FaCog,
  FaThermometerHalf,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaTools,
  FaDatabase,
  FaBrain,
  // Phase 4: Action Hub icons
  FaWrench,
  FaHistory,
  FaChartLine,
  FaMap, // Phase 5: Zone boundary toggle icon
} from 'react-icons/fa';
import {
  MdSensors,
  MdGpsFixed,
  MdRefresh,
  // --- UPDATE
  MdAutoAwesome,
  MdOpenInNew,
  MdHelpOutline,
  // --- END UPDATE
} from 'react-icons/md';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapContainer,
  TileLayer,
  Marker,
  ZoomControl,
  useMap,
  CircleMarker,
  GeoJSON, // Phase 5: Render zone polygon boundaries
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
// Marker clustering — groups nearby pins into a single numbered circle when
// zoomed out, then explodes them back to individual markers on zoom-in.
// Install: npm install react-leaflet-cluster
import MarkerClusterGroup from 'react-leaflet-cluster';
import { authenticatedFetch } from '../../services/api';
// Active Work Order check — prevents duplicate ticket creation across sessions
import workOrderService from '../../services/workOrderService';

// --- REAL-TIME: Import the WebSocket notification context ---
import { useNotifications } from '../../contexts/NotificationContext';

// --- UPDATE: FlowSense ecosystem — live events, ML, AI, typed work orders, basemaps
import { useNavigate } from 'react-router-dom';
import webSocketService from '../../services/websocketService';
import useFlowSenseLive from '../../hooks/useFlowSenseLive';
import { publish as publishBus, BUS_EVENTS } from '../../utils/flowsenseBus';
import { fetchAssetsAtRisk } from '../../services/predictiveAlertService';
import { getFlowSenseAiStatus } from '../../services/flowsenseAiService';
import { buildDraft, normalizeDraft, suggestTitle, openCreateWorkOrder, typesFor, PRIORITY_RANK } from '../../components/work_orders/workOrderCatalog';
import { useFlowSenseIntel } from '../../components/dashboard/ai/FlowSenseIntelContext';
import DashboardAiHelper from '../../components/dashboard/ai/DashboardAiHelper';
import {
  TILE_LAYERS, TILE_GROUPS, TILE_FAILURE_THRESHOLD, defaultTileKey, saveTileKey, tileUrlFor,
} from '../../components/dashboard/livegrid/liveGridTiles';
import {
  LIVE_GRID_EVENTS, LIVE_ALERT_TTL_MS, normalizeLivePatch, indexRisk, mergeRiskIntoAssets,
  buildGridBrief, assessAsset,
} from '../../components/dashboard/livegrid/liveGridIntel';
import GridAiPanel, { GridAiStrip } from '../../components/dashboard/livegrid/GridAiPanel';
import AssetAiInsight from '../../components/dashboard/livegrid/AssetAiInsight';
// --- END UPDATE

// --- Phase 4 Step 3: Telemetry sparkline chart component ---
import AssetTelemetryChart from '../../components/dashboard/AssetTelemetryChart';

import AssetPopupCard from '../../components/dashboard/AssetPopupCard';
import SensorMapLayer from '../../components/dashboard/SensorMapLayer';

// --- Maintenance History Modal: lazy-loaded so the full ~200KB module +
//     GPU compositor layers only mount when the modal is actually opened,
//     not on initial LiveGrid page load alongside the Leaflet map.
// TODO: Adjust the path to match where MaintenanceHistory.jsx lives in your project.
const MaintenanceHistory = React.lazy(
  () => import('../../components/public_utilities/MaintenanceHistory')
);

// ============================================================================
// MOTION WRAPPERS
// ============================================================================
const MotionBox = motion(Box);

// ============================================================================
// FIX: Leaflet default icon path issue in bundlers
// ============================================================================
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// ============================================================================
// CUSTOM SVG MAP ICONS — Enterprise grade, crisp at any zoom
// ============================================================================
const createSVGIcon = (svgPath, fillColor, strokeColor = '#fff') => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="16" fill="${fillColor}" stroke="${strokeColor}" stroke-width="2.5" opacity="0.95"/>
      <g transform="translate(18,18) scale(0.45)" fill="white" stroke="none">
        <g transform="translate(-20,-20)">
          ${svgPath}
        </g>
      </g>
    </svg>`;
  return L.divIcon({
    className: 'custom-svg-icon',
    html: svg,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -20],
  });
};

// SVG paths for each asset type (40x40 viewBox centered)
const SVG_PATHS = {
  pump: `<path d="M8 12h6v-2H8V8l-4 4 4 4v-2h6v-2H8v0zm26 0c0-1.1-.9-2-2-2h-4v4h4c1.1 0 2-.9 2-2zm-12-8c-4.42 0-8 3.58-8 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6s2.69-6 6-6 6 2.69 6 6-2.69 6-6 6z" transform="translate(2,2)"/>`,
  sensor: `<path d="M20 2c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2s2-.9 2-2V4c0-1.1-.9-2-2-2zm-7.07 2.93c-.39-.39-1.02-.39-1.41 0-.39.39-.39 1.02 0 1.41L13.17 8c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41l-1.65-1.66zm14.14 0c-.39-.39-1.02-.39-1.41 0L24.01 6.59c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0l1.65-1.66c.39-.39.39-1.02 0-1.41zM4 12c0 1.1.9 2 2 2h4c1.1 0 2-.9 2-2s-.9-2-2-2H6c-1.1 0-2 .9-2 2zm8 8c0-1.1-.9-2-2-2H6c-1.1 0-2 .9-2 2s.9 2 2 2h4c1.1 0 2-.9 2-2zm14-8c0-1.1-.9-2-2-2h-4c1.1 0 2 .9 2 2s-.9 2-2 2h4c1.1 0 2-.9 2-2zM20 18c-1.1 0-2 .9-2 2v4c0 1.1.9 2 2 2s2-.9 2-2v-4c0-1.1-.9-2-2-2z" transform="translate(0,0) scale(0.95)"/>`,
  tank: `<path d="M18.67 2c-.35 0-.69.12-.95.34l-1.06.93c-.41.36-.64.88-.64 1.43v1.97L8 13V3H6v10L2 16.33V18h.33L6 22h12l3.67-4H22v-1.67L18 13V6.7c0-.55-.23-1.07-.64-1.43l-1.06-.93A1.49 1.49 0 0018.67 2z" transform="translate(2,2) scale(1)"/>`,
  valve: `<path d="M20 14h-2V8h-2v2H8V8H6v6H4v2h2v6h2v-2h8v2h2v-6h2v-2zm-6 4H10v-4h4v4z" transform="translate(2,0)"/>`,
  pipeline: `<path d="M4 10h12v4H4zm14-2h4c1.1 0 2 .9 2 2v4c0 1.1-.9 2-2 2h-4v-2h4v-4h-4V8zM2 8h4v2H2c-1.1 0-2 .9-2 2v0c0 1.1.9 2 2 2h4v2H2c-2.21 0-4-1.79-4-4v0c0-2.21 1.79-4 4-4z" transform="translate(4,4)"/>`,
  meter: `<path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zm-8-2l4-8h-3V5h-2v3H8l4 8z" transform="translate(2,2)"/>`,
  hydrant: `<path d="M19 11h-1V8h2V6h-2V4c0-.55-.45-1-1-1h-4V1h-2v2H7c-.55 0-1 .45-1 1v2H4v2h2v3H5c-1.1 0-2 .9-2 2v2h2v4h2v-4h10v4h2v-4h2v-2c0-1.1-.9-2-2-2zm-3 0H8V6h8v5z" transform="translate(2,2)"/>`,
  // Phase 5: Macro-infrastructure types
  well:        `<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5-9h10v2H7z" transform="translate(6,6) scale(0.6)"/>`,
  bridge:      `<path d="M22 16v-4c0-3.31-2.69-6-6-6h-8c-3.31 0-6 2.69-6 6v4H0v2h2v4h2v-4h16v4h2v-4h2v-2h-2zm-18-4c0-2.21 1.79-4 4-4h8c2.21 0 4 1.79 4 4v2H4v-2z" transform="translate(0,4) scale(0.9)"/>`,
  catch_basin: `<path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14H7v-2h5v2zm4-4H7v-2h9v2zm0-4H7V7h9v2z" transform="translate(2,2) scale(0.85)"/>`,
  default: `<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" transform="translate(2,0) scale(1.1)"/>`,
};

const ASSET_COLORS = {
  pump: '#3182CE',
  sensor: '#805AD5',
  tank: '#38A169',
  valve: '#DD6B20',
  pipeline: '#319795',
  meter: '#D53F8C',
  hydrant: '#E53E3E',
  // Phase 5: Macro-infrastructure types
  well:        '#2B6CB0', // Deep Blue
  bridge:      '#718096', // Concrete Gray
  catch_basin: '#4FD1C5', // Cyan/Teal
  default: '#718096',
};

// The strict allowlist of infrastructure types recognised by the Command Center.
// fetchAssets uses this to strip out any asset category that doesn't belong on
// the water-utility map (e.g. office furniture rows accidentally imported via CSV).
const ALLOWED_INFRASTRUCTURE_TYPES = [
  'pump', 'sensor', 'tank', 'valve', 'pipeline',
  'meter', 'hydrant', 'well', 'bridge', 'catch_basin', 'catch basin',
];

// ============================================================================
// AI HEALTH SCORE COLORS
// ============================================================================
const AI_HEALTH_COLORS = {
  optimal: '#38A169',   // Green  — 80-100%
  warning: '#DD6B20',   // Orange — 40-79%
  critical: '#E53E3E',  // Red    — 0-39%
};

const getHealthColor = (healthScore) => {
  const score = healthScore ?? 100;
  if (score >= 80) return AI_HEALTH_COLORS.optimal;
  if (score >= 40) return AI_HEALTH_COLORS.warning;
  return AI_HEALTH_COLORS.critical;
};

const getHealthLabel = (healthScore) => {
  const score = healthScore ?? 100;
  if (score >= 80) return 'Optimal';
  if (score >= 40) return 'Deteriorating';
  return 'Critical Risk';
};

const getHealthColorScheme = (healthScore) => {
  const score = healthScore ?? 100;
  if (score >= 80) return 'green';
  if (score >= 40) return 'orange';
  return 'red';
};

// ============================================================================
// STANDARD ASSET HELPERS (type-based)
// ============================================================================
const getAssetIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('pump')) return createSVGIcon(SVG_PATHS.pump, ASSET_COLORS.pump);
  if (t.includes('sensor') || t.includes('meter')) return createSVGIcon(SVG_PATHS.sensor, ASSET_COLORS.sensor);
  if (t.includes('tank') || t.includes('reservoir')) return createSVGIcon(SVG_PATHS.tank, ASSET_COLORS.tank);
  if (t.includes('valve')) return createSVGIcon(SVG_PATHS.valve, ASSET_COLORS.valve);
  if (t.includes('pipe') || t.includes('main')) return createSVGIcon(SVG_PATHS.pipeline, ASSET_COLORS.pipeline);
  if (t.includes('hydrant')) return createSVGIcon(SVG_PATHS.hydrant, ASSET_COLORS.hydrant);
  if (t.includes('well')) return createSVGIcon(SVG_PATHS.well, ASSET_COLORS.well);
  if (t.includes('bridge')) return createSVGIcon(SVG_PATHS.bridge, ASSET_COLORS.bridge);
  if (t.includes('catch') || t.includes('basin')) return createSVGIcon(SVG_PATHS.catch_basin, ASSET_COLORS.catch_basin);
  return createSVGIcon(SVG_PATHS.default, ASSET_COLORS.default);
};

const getAssetColor = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('pump')) return ASSET_COLORS.pump;
  if (t.includes('sensor') || t.includes('meter')) return ASSET_COLORS.sensor;
  if (t.includes('tank') || t.includes('reservoir')) return ASSET_COLORS.tank;
  if (t.includes('valve')) return ASSET_COLORS.valve;
  if (t.includes('pipe') || t.includes('main')) return ASSET_COLORS.pipeline;
  if (t.includes('hydrant')) return ASSET_COLORS.hydrant;
  if (t.includes('well')) return ASSET_COLORS.well;
  if (t.includes('bridge')) return ASSET_COLORS.bridge;
  if (t.includes('catch') || t.includes('basin')) return ASSET_COLORS.catch_basin;
  return ASSET_COLORS.default;
};

const getAssetReactIcon = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('pump')) return FaCog;
  if (t.includes('sensor') || t.includes('meter')) return MdSensors;
  if (t.includes('tank') || t.includes('reservoir') || t.includes('well')) return FaWater;
  if (t.includes('valve')) return FaTools;
  if (t.includes('pipe') || t.includes('main')) return FaDatabase;
  if (t.includes('hydrant') || t.includes('catch') || t.includes('basin')) return FaThermometerHalf;
  return FaMapMarkerAlt;
};

const STATUS_CONFIG = {
  operational: { color: 'green', icon: FaCheckCircle, label: 'Operational' },
  active: { color: 'green', icon: FaCheckCircle, label: 'Active' },
  warning: { color: 'orange', icon: FaExclamationTriangle, label: 'Warning' },
  maintenance: { color: 'yellow', icon: FaTools, label: 'Maintenance' },
  critical: { color: 'red', icon: FaTimesCircle, label: 'Critical' },
  offline: { color: 'gray', icon: FaTimesCircle, label: 'Offline' },
  inactive: { color: 'gray', icon: FaTimesCircle, label: 'Inactive' },
};

const getStatusConfig = (status) => {
  const normalized = (status || '').toLowerCase();
  return STATUS_CONFIG[normalized] || { color: 'blue', icon: FaCheckCircle, label: status || 'Unknown' };
};

// ============================================================================
// MAP TILE LAYERS
// --- UPDATE: moved to components/dashboard/livegrid/liveGridTiles.js (CARTO API
// key basemaps + imagery + terrain, remembered choice, automatic fallback).
// --- END UPDATE
// ============================================================================

// ============================================================================
// DEFAULT CENTER — Nassau, Bahamas
// ============================================================================
const DEFAULT_CENTER = [25.0343, -77.3963];
const DEFAULT_ZOOM = 12;
const AUTO_REFRESH_INTERVAL = 30000;

// ============================================================================
// HELPER: MapController
// ============================================================================
function MapController({ flyTo }) {
  const map = useMap();
  useEffect(() => {
    if (flyTo) {
      map.flyTo(flyTo, 16, { duration: 1.2 });
    }
  }, [flyTo, map]);
  return null;
}

// ============================================================================
// SUB-COMPONENT: GlassPanel
// ============================================================================
const GlassPanel = ({ children, ...props }) => {
  const bg = useColorModeValue('rgba(255,255,255,0.85)', 'rgba(26,32,44,0.88)');
  const border = useColorModeValue('rgba(255,255,255,0.6)', 'rgba(255,255,255,0.08)');

  return (
    <Box
      bg={bg}
      backdropFilter="blur(16px)"
      borderWidth="1px"
      borderColor={border}
      borderRadius="xl"
      boxShadow="lg"
      {...props}
    >
      {children}
    </Box>
  );
};

// ============================================================================
// SUB-COMPONENT: AssetListItem
// ============================================================================
const AssetListItem = React.memo(({ asset, isSelected, onSelect, aiMode, liveAlert }) => {
  const hoverBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const selectedBg = useColorModeValue('blue.100', 'whiteAlpha.200');
  const textColor = useColorModeValue('gray.800', 'white');
  const subColor = useColorModeValue('gray.500', 'gray.400');
  const statusCfg = getStatusConfig(asset.status);
  const AssetIcon = getAssetReactIcon(asset.type);
  const assetColor = getAssetColor(asset.type);

  return (
    <MotionBox whileHover={{ x: 4 }} transition={{ duration: 0.15 }}>
      <Flex
        align="center"
        px={3}
        py={2.5}
        cursor="pointer"
        borderRadius="lg"
        bg={isSelected ? selectedBg : 'transparent'}
        _hover={{ bg: isSelected ? selectedBg : hoverBg }}
        onClick={() => onSelect(asset)}
        transition="background 0.15s"
        gap={3}
      >
        {/* Asset type icon */}
        <Flex
          w="36px"
          h="36px"
          borderRadius="lg"
          bg={aiMode ? `${getHealthColor(asset.health_score)}20` : `${assetColor}20`}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon
            as={aiMode ? FaBrain : AssetIcon}
            color={aiMode ? getHealthColor(asset.health_score) : assetColor}
            boxSize={4}
          />
        </Flex>

        {/* Info */}
        <Box flex="1" minW={0}>
          <HStack spacing={1}>
            <Text fontSize="sm" fontWeight="600" color={textColor} noOfLines={1}>
              {asset.name}
            </Text>
            {asset.last_psi != null && (
              <Badge colorScheme="blue" fontSize="2xs" borderRadius="full" px={1.5}>
                {asset.last_psi} PSI
              </Badge>
            )}
            {/* --- UPDATE: live anomaly (WebSocket) + ML failure risk */}
            {liveAlert && (
              <Badge colorScheme="red" variant="solid" fontSize="2xs" borderRadius="full" px={1.5} title={liveAlert.message}>
                LIVE
              </Badge>
            )}
            {asset.risk_score != null && asset.risk_score >= 60 && (
              <Badge colorScheme="purple" fontSize="2xs" borderRadius="full" px={1.5} title={asset.risk_description || 'ML failure risk'}>
                ML {asset.risk_score}%
              </Badge>
            )}
            {/* --- END UPDATE */}
            {aiMode && asset.health_score != null && (
              <Badge
                colorScheme={getHealthColorScheme(asset.health_score)}
                fontSize="2xs"
                borderRadius="full"
                px={1.5}
              >
                {asset.health_score}%
              </Badge>
            )}
          </HStack>
          <Text fontSize="xs" color={subColor} noOfLines={1}>
            {asset.type || 'Asset'} • {asset.zone_name || asset.location_description || 'No zone'}
          </Text>
        </Box>

        {/* Status Badge — text-readable, strictly driven by the database value.
            In AI mode shows the health %, in standard mode shows the status label. */}
        <Badge
          colorScheme={aiMode ? getHealthColorScheme(asset.health_score) : statusCfg.color}
          fontSize="2xs"
          borderRadius="full"
          px={2}
          py={0.5}
          ml={1}
          flexShrink={0}
          textTransform="capitalize"
        >
          {aiMode
            ? `${asset.health_score ?? 'N/A'}%`
            : statusCfg.label}
        </Badge>
      </Flex>
    </MotionBox>
  );
});

AssetListItem.displayName = 'AssetListItem';

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function LiveGrid() {
  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [popupKey, setPopupKey] = useState(0); // Forces popup to reopen on click
  const [flyTo, setFlyTo] = useState(null);
  // --- UPDATE: remembered basemap (Dark Matter in dark mode, Voyager in light mode by default)
  const { colorMode } = useColorMode();
  const [tileLayer, setTileLayerState] = useState(() => defaultTileKey(colorMode));
  const setTileLayer = useCallback((key) => {
    if (!TILE_LAYERS[key]) return;
    setTileLayerState(key);
    saveTileKey(key);
  }, []);
  // Tile health: loaded flag + keyed-endpoint failure → automatic keyless fallback
  const [tileLoaded, setTileLoaded] = useState(false);
  const [tileError, setTileError] = useState(null);
  const [tileFallback, setTileFallback] = useState({}); // { [tileKey]: true }
  const tileStatsRef = useRef({ errors: 0, loaded: false });
  // --- END UPDATE
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [pulsingAssetId, setPulsingAssetId] = useState(null);

  // --- AI LENS: Toggle between standard type-coloring and health-score coloring ---
  const [aiMode, setAiMode] = useState(false);

  // --- Phase 4 Step 3: Asset telemetry history for the sparkline chart ---
  const [assetHistory, setAssetHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // --- Ollama AI Triage: loading state for the triage brief generation ---
  const [isGeneratingTriage, setIsGeneratingTriage] = useState(false);

  // --- Active Work Order: tracks an open ticket for the selected asset so the
  //     Generate button locks immediately and persists across drawer re-opens
  //     via a DB check in handleSelectAsset ---
  const [activeWorkOrder, setActiveWorkOrder] = useState(null);

  // Popup card work order — lightweight fetch triggered on marker click,
  // separate from the full drawer WO check so the popup can load instantly.
  const [activeAssetWO, setActiveAssetWO] = useState(null);
  const [isLoadingWO, setIsLoadingWO] = useState(false);

  // --- Phase 5: Geographical zone boundaries ---
  const [assignmentZones, setAssignmentZones] = useState([]);
  const [showZones, setShowZones] = useState(false);

  const [rawSensors, setRawSensors] = useState([]);
  const [pipelines, setPipelines] = useState([]);

  // Phase 5: Spatial Analytics — tracks the zone the admin clicked on
  const [selectedZone, setSelectedZone] = useState(null);
  // Phase 5: Recalculate button loading state for the floating zone card
  const [isRecalculating, setIsRecalculating] = useState(false);

  const toast = useToast();
  const refreshTimerRef = useRef(null);
  const pulseTimerRef = useRef(null);

  // --- REAL-TIME: Consume WebSocket context ---
  const { isConnected } = useNotifications();

  // --- UPDATE: FlowSense ecosystem state
  const navigate = useNavigate();
  const { brief: systemBrief, feed } = useFlowSenseIntel();
  const [riskIndex, setRiskIndex] = useState({});          // ML assets-at-risk by asset id
  const [liveAlerts, setLiveAlerts] = useState({});        // { [assetId]: { severity, message, at } }
  const [aiStatus, setAiStatus] = useState(null);          // { ollama, ml } from /flowsense-ai/status
  const { isOpen: isGridAiOpen, onOpen: onGridAiOpen, onClose: onGridAiClose } = useDisclosure();
  const { isOpen: isGuideOpen, onOpen: onGuideOpen, onClose: onGuideClose } = useDisclosure();
  const isMountedRef = useRef(true);
  useEffect(() => { isMountedRef.current = true; return () => { isMountedRef.current = false; }; }, []);
  // --- END UPDATE

  // --- PHASE 4: Command Drawer disclosure ---
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();

  // --- Maintenance History Modal disclosure ---
  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();

  // --------------------------------------------------------------------------
  // THEME — all hooks at top level
  // --------------------------------------------------------------------------
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headingColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const inputBg = useColorModeValue('white', 'gray.700');
  const panelGradient = useColorModeValue(
    'linear(to-br, blue.500, purple.600)',
    'linear(to-br, blue.400, purple.500)'
  );
  const aiGradient = useColorModeValue(
    'linear(to-br, purple.500, pink.500)',
    'linear(to-br, purple.400, pink.400)'
  );
  const statBg = useColorModeValue('gray.50', 'gray.700');
  const scrollTrackBg = useColorModeValue('gray.100', 'gray.700');
  const scrollThumbBg = useColorModeValue('gray.300', 'gray.600');
  const aiModeBg = useColorModeValue('purple.50', 'purple.900');
  const aiModeBorder = useColorModeValue('purple.300', 'purple.600');
  const drawerSectionBg = useColorModeValue('gray.50', 'gray.750');
  const aiSectionBg = useColorModeValue('purple.50', 'rgba(128,90,213,0.12)'); // --- UPDATE
  // Smart UI: bg for non-telemetry geospatial placeholder
  const geoPlaceholderBg = useColorModeValue('gray.50', 'gray.750');
  const geoIconBg = useColorModeValue('gray.100', 'gray.700');
  // Maintenance History Modal body background
  const modalBodyBg = useColorModeValue('gray.50', 'gray.900');
  // Active Work Order status block colors
  const activeWoBg = useColorModeValue('blue.50', 'rgba(49, 130, 206, 0.15)');
  const activeWoBorder = useColorModeValue('blue.200', 'blue.700');
  const activeWoText = useColorModeValue('blue.700', 'blue.300');
  const activeWoTitle = useColorModeValue('blue.900', 'blue.100');

  // --------------------------------------------------------------------------
  // AI-AWARE DYNAMIC COLOR — switches between type-based and health-based
  // --------------------------------------------------------------------------
  const getDynamicColor = useCallback(
    (asset) => {
      if (!asset) return ASSET_COLORS.default;
      if (!aiMode) return getAssetColor(asset.type);
      return getHealthColor(asset.health_score);
    },
    [aiMode]
  );

  const getDynamicIcon = useCallback(
    (asset) => {
      if (!aiMode) return getAssetIcon(asset.type);
      // In AI mode, use generic dot markers so color is the focal point
      return createSVGIcon(SVG_PATHS.default, getHealthColor(asset.health_score));
    },
    [aiMode]
  );

  // --------------------------------------------------------------------------
  // STEP 1 — Smart UI Filtering: determine if the selected asset has live
  // telemetry capability (sensor/meter type OR has a known last_psi reading).
  // This memo recalculates whenever selectedAsset changes.
  // --------------------------------------------------------------------------
  const hasTelemetry = useMemo(() => {
    if (!selectedAsset) return false;
    const type = (selectedAsset.type || '').toLowerCase();
    return (
      type.includes('sensor') ||
      type.includes('meter') ||
      selectedAsset.last_psi != null
    );
  }, [selectedAsset]);

  // --------------------------------------------------------------------------
  // DATA FETCHING
  // --------------------------------------------------------------------------
  // Pagination state for the sidebar — the map always shows ALL assets,
  // only the sidebar list is sliced so it stays snappy with large datasets.
  const [currentPage, setCurrentPage] = useState(1);
  const ASSETS_PER_PAGE = 20;

  // ==========================================================================
  // FETCH DATA
  // ==========================================================================
  const fetchAssets = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);

    try {
      // ?all=true bypasses backend pagination so every asset appears on the map
      const response = await authenticatedFetch('/assets?all=true');

      // Handle all three common response shapes:
      //   { assets: [...] }  — paginated envelope with 'assets' key
      //   { data: [...] }    — paginated envelope with 'data' key
      //   [...]              — bare array
      const assetData =
        response?.assets ||
        response?.data ||
        (Array.isArray(response) ? response : []);

      const mappableAssets = assetData
        .filter((a) => {
          // 1. Must have valid GPS coordinates
          const hasValidCoords =
            a.latitude != null &&
            a.longitude != null &&
            !isNaN(Number(a.latitude)) &&
            !isNaN(Number(a.longitude));

          if (!hasValidCoords || !a.type) return false;

          // 2. Must strictly match the permitted infrastructure allowlist —
          //    strips out any non-utility asset category (e.g. bad CSV imports).
          const typeLower = a.type.toLowerCase();
          return ALLOWED_INFRASTRUCTURE_TYPES.some(
            (allowed) =>
              typeLower.includes(allowed) ||
              typeLower.includes(allowed.replace('_', ' '))
          );
        })
        .map((a) => ({
          ...a,
          latitude: parseFloat(a.latitude),
          longitude: parseFloat(a.longitude),
        }));

      // ── SMART DIFFING ENGINE ─────────────────────────────────────────────────
      // Only call setAssets when the database actually shows a change. This
      // prevents React from re-rendering the Leaflet map (and flickering all
      // markers) on every 30-second auto-refresh when nothing has changed.
      setAssets((prevAssets) => {
        // First load — nothing to diff against
        if (prevAssets.length === 0) return mappableAssets;

        let hasChanges = false;

        // A new asset was added or removed
        if (prevAssets.length !== mappableAssets.length) {
          hasChanges = true;
        } else {
          // Per-asset deep check: status, health score, or PSI changed in DB?
          for (const incoming of mappableAssets) {
            const existing = prevAssets.find((p) => p.id === incoming.id);
            if (
              !existing ||
              existing.status !== incoming.status ||
              existing.health_score !== incoming.health_score ||
              existing.last_psi !== incoming.last_psi
            ) {
              hasChanges = true;
              break;
            }
          }
        }

        // Return prevAssets unchanged if nothing diffed — prevents map flicker
        return hasChanges ? mappableAssets : prevAssets;
      });

      // Keep the open Drawer strictly synced with the database so the health
      // score and status badge never show stale data after a background refresh.
      setSelectedAsset((prev) => {
        if (!prev) return prev;
        const refreshed = mappableAssets.find((a) => a.id === prev.id);
        if (
          refreshed &&
          (refreshed.status !== prev.status ||
            refreshed.health_score !== prev.health_score)
        ) {
          return { ...prev, status: refreshed.status, health_score: refreshed.health_score };
        }
        return prev;
      });

      setLastRefreshTime(new Date());
    } catch (error) {
      console.error('LiveGrid: Failed to load assets:', error);
      if (!silent) {
        toast({
          title: 'Failed to Load Assets',
          description: 'Could not fetch asset data. Check your network connection.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [toast]);

  // PHASE 5: Fetch operational zone boundaries from the backend.
  // Each zone row has a polygonGeoJSON field containing a GeoJSON geometry
  // (Polygon or MultiPolygon). We parse it here and store only valid shapes.
  const fetchZones = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/assignment-zones');
      const zonesData = Array.isArray(response) ? response : (response?.data || []);

      const mappedZones = zonesData.map((zone) => {
        let parsedGeo = null;
        // Use 'polygonGeoJSON' to match the backend model field name
        if (zone.polygonGeoJSON) {
          try {
            parsedGeo =
              typeof zone.polygonGeoJSON === 'string'
                ? JSON.parse(zone.polygonGeoJSON)
                : zone.polygonGeoJSON;
          } catch (e) {
            console.warn(`LiveGrid: Failed to parse GeoJSON for zone ${zone.id}`, e);
          }
        }
        return { ...zone, parsedGeometry: parsedGeo };
      });

      setAssignmentZones(mappedZones.filter((z) => z.parsedGeometry !== null));
    } catch (error) {
      console.error('LiveGrid: Failed to load assignment zones', error);
    }
  }, []);

  const fetchRawInfrastructure = useCallback(async () => {
    try {
      // Fetch both pipelines and raw sensors in parallel
      const [pipesRes, sensRes] = await Promise.allSettled([
        authenticatedFetch('/pipelines'),
        authenticatedFetch('/sensors') // Fetches from your sensorController.getAllSensors
      ]);

      if (pipesRes.status === 'fulfilled') {
        const pData = pipesRes.value?.data || pipesRes.value || [];
        setPipelines(Array.isArray(pData) ? pData : []);
      }
      
      if (sensRes.status === 'fulfilled') {
        const sData = sensRes.value?.data || sensRes.value || [];
        setRawSensors(Array.isArray(sData) ? sData : []);
      }
    } catch (error) {
      console.error('LiveGrid: Failed to load raw infrastructure', error);
    }
  }, []);

  // --- UPDATE: ML failure model ("assets at risk") → merged into every asset by id
  const loadRisk = useCallback(async () => {
    try {
      const res = await fetchAssetsAtRisk();
      const rows = Array.isArray(res) ? res : (res?.data || []);
      if (isMountedRef.current) setRiskIndex(indexRisk(rows));
    } catch (e) {
      console.warn('LiveGrid: ML risk unavailable', e?.message || e);
    }
  }, []);

  // --- UPDATE: Ollama + ML engine status for the Grid AI panel (cached 15 s server-side)
  const loadAiStatus = useCallback(async () => {
    try {
      const st = await getFlowSenseAiStatus();
      if (isMountedRef.current) setAiStatus(st || null);
    } catch {
      if (isMountedRef.current) setAiStatus(null);
    }
  }, []);
  // --- END UPDATE

  // PHASE 5: Trigger backend spatial recalculation for all zones.
  // POSTs to /assignment-zones/recalculate, then re-fetches zones so the
  // floating card shows up-to-date asset counts and health scores.
  const handleRecalculateZone = useCallback(async () => {
    setIsRecalculating(true);
    try {
      await authenticatedFetch('/assignment-zones/recalculate', { method: 'POST' });

      // Re-fetch so the new numbers are available on next polygon click
      await fetchZones();

      // Dismiss the card — user clicks the polygon again to see fresh data
      setSelectedZone(null);

      toast({
        title: 'Spatial Analytics Updated',
        description: 'Successfully counted all assets and work orders inside boundaries.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error('LiveGrid: Failed to recalculate zone stats', err);
      toast({
        title: 'Failed to Recalculate',
        description: 'Could not reach the recalculate endpoint. Check your backend.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsRecalculating(false);
    }
  }, [fetchZones, toast]);

  // --------------------------------------------------------------------------
  // PHASE 5: SMART SYNC ENGINE
  // Must be declared AFTER fetchZones — useCallback closes over it and
  // const bindings aren't hoisted (TDZ error if placed before fetchZones).
  //
  // Debounces expensive spatial math so that a burst of WebSocket events
  // (e.g. 50 health-score updates in 1 s) only triggers ONE backend
  // recalculate, 2.5 s after the last event settles.
  // --------------------------------------------------------------------------

  // Ref holds the pending debounce timer so it can be cancelled on new events
  const zoneRecalcTimeoutRef = useRef(null);

  // High-performance WebSocket buffers — useRef so writes never trigger renders.
  // The Flush Engine drains these once per second in a single batched setState.
  const pendingUpdatesRef = useRef({});      // { [assetId]: { status?, last_psi?, health_score?, ... } }
  const pendingPulsesRef  = useRef(new Set()); // Set of assetIds that received a pressure reading
  // --- UPDATE: live anomalies buffered the same way (flushed with the 1 s engine)
  const pendingAlertsRef  = useRef({});       // { [assetId]: { severity, message, at } }
  const assetsRef = useRef([]);               // latest assets for sensor → asset matching
  const selectedAssetIdRef = useRef(null);
  const refreshSelectedWoRef = useRef(null);
  // --- END UPDATE

  const triggerZoneRecalculation = useCallback(() => {
    if (zoneRecalcTimeoutRef.current) clearTimeout(zoneRecalcTimeoutRef.current);
    zoneRecalcTimeoutRef.current = setTimeout(async () => {
      try {
        await authenticatedFetch('/assignment-zones/recalculate', { method: 'POST' });
        await fetchZones();
      } catch (e) {
        console.warn('LiveGrid: Background zone recalculation failed', e);
      }
    }, 2500);
  }, [fetchZones]);

  // --------------------------------------------------------------------------
  // PHASE 5: LIVE CARD UPDATE
  // When assignmentZones is refreshed by fetchZones(), patch the open floating
  // Zone Card so counts tick live without closing and reopening the card.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedZone) return;
    const refreshed = assignmentZones.find((z) => z.id === selectedZone.id);
    if (refreshed) {
      setSelectedZone((prev) => ({
        ...prev,
        total_assets:         refreshed.total_assets,
        active_work_orders:   refreshed.active_work_orders,
        average_health_score: refreshed.average_health_score,
        asset_breakdown:      refreshed.asset_breakdown,
      }));
    }
    // Intentionally omit selectedZone — react only when the zone list changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentZones]);

  // 1. Fetch assets and zones immediately on mount/refresh — empty dep array
  //    ensures this fires exactly once, guaranteeing instant load without
  //    waiting for the 2.5-second zone-calculation debounce.
  useEffect(() => {
    fetchAssets();
    fetchZones();
    // --- UPDATE: pipelines + raw sensors (fetchRawInfrastructure was defined but never called),
    // ML risk and AI engine status
    fetchRawInfrastructure();
    loadRisk();
    loadAiStatus();
    // --- END UPDATE
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. Trigger heavy spatial math in the background after the initial paint
  useEffect(() => {
    triggerZoneRecalculation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      refreshTimerRef.current = setInterval(() => {
        fetchAssets(true);
      }, AUTO_REFRESH_INTERVAL);
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [autoRefresh, fetchAssets]);

  // ==========================================================================
  // WEBSOCKET LISTENER — SILENT BUFFER
  // ==========================================================================
  // All incoming WS events are written into useRef buckets (no React renders).
  // The Flush Engine below drains those buckets once per second in a single
  // batched setState, capping UI updates to 1 render/s regardless of how many
  // events arrive. 50 sensors reporting per second → 1 render, not 50.
  // ==========================================================================
  useEffect(() => {
    const handleRealTimeUpdate = (event) => {
      const data = event.detail;
      if (!data || !data.type || !data.payload) return;

      const {
        asset_id, assetId,
        status, psi,
        health_score, predicted_failure_date, remaining_useful_life,
      } = data.payload;

      const targetId = asset_id || assetId;
      if (!targetId) return;

      // Initialise the buffer slot for this asset if it doesn't exist yet
      if (!pendingUpdatesRef.current[targetId]) {
        pendingUpdatesRef.current[targetId] = {};
      }

      // Silently merge into the buffer — no setState, no re-render
      if (data.type === 'asset_status_update') {
        pendingUpdatesRef.current[targetId].status = status;
        triggerZoneRecalculation();
      } else if (data.type === 'new_pressure_reading') {
        pendingUpdatesRef.current[targetId].last_psi = psi;
        pendingPulsesRef.current.add(targetId);
      } else if (data.type === 'health_score_update') {
        pendingUpdatesRef.current[targetId].health_score          = health_score;
        pendingUpdatesRef.current[targetId].predicted_failure_date = predicted_failure_date;
        pendingUpdatesRef.current[targetId].remaining_useful_life  = remaining_useful_life;
        triggerZoneRecalculation();
      } else if (
        data.type === 'work_order_status_updated' ||
        data.type === 'work_order_assigned'
      ) {
        triggerZoneRecalculation();
      }
    };

    window.addEventListener('ws_message', handleRealTimeUpdate);
    return () => window.removeEventListener('ws_message', handleRealTimeUpdate);
  }, [triggerZoneRecalculation]);

  // ==========================================================================
  // --- UPDATE: DIRECT WEBSOCKET SUBSCRIPTION — the events the backend really
  // emits (asset_prediction_update, anomaly_recorded, critical_anomaly_detected,
  // work_order_events, asset / utility updates, readings). Same silent buffers,
  // same 1 s flush engine — a burst of 500 events is still one render per second.
  // ==========================================================================
  useEffect(() => {
    const findAssetId = (info) => {
      if (info.assetId) return info.assetId;
      if (!info.sensorId) return null;
      const match = assetsRef.current.find((a) =>
        String(a.sensor_id ?? a.device_id ?? a.serial_number ?? '') === info.sensorId ||
        ((a.type || '').toLowerCase().includes('sensor') && String(a.id) === info.sensorId));
      return match ? String(match.id) : null;
    };
    const ingest = (name) => (data) => {
      const info = normalizeLivePatch(name, data);
      if (!info) return;
      if (info.kind === 'workOrder') {
        triggerZoneRecalculation();
        // Keep the open drawer's "Current Work Order" block in sync
        if (selectedAssetIdRef.current && (!info.assetId || info.assetId === String(selectedAssetIdRef.current))) {
          refreshSelectedWoRef.current?.();
        }
        return;
      }
      const id = findAssetId(info);
      if (!id) return;
      if (Object.keys(info.patch).length) {
        pendingUpdatesRef.current[id] = { ...(pendingUpdatesRef.current[id] || {}), ...info.patch };
      }
      if (info.pulse) pendingPulsesRef.current.add(id);
      if (info.alert) pendingAlertsRef.current[id] = info.alert;
      if (info.kind === 'status' || info.kind === 'health') triggerZoneRecalculation();
    };
    const handlers = LIVE_GRID_EVENTS.map((name) => {
      const h = ingest(name);
      webSocketService?.subscribe?.(name, h);
      return [name, h];
    });
    return () => handlers.forEach(([n, h]) => webSocketService?.unsubscribe?.(n, h));
  }, [triggerZoneRecalculation]);

  // Debounced refetches: new / deleted assets, fresh ML predictions, engine health
  useFlowSenseLive(['assets'], () => fetchAssets(true), { debounceMs: 3000 });
  useFlowSenseLive(['predictions'], () => loadRisk(), { debounceMs: 5000 });
  useFlowSenseLive(['health', 'status'], () => loadAiStatus(), { debounceMs: 3000, includeBus: false });
  // --- END UPDATE

  // ==========================================================================
  // FLUSH ENGINE — 1 FPS BATCH RENDERER
  // ==========================================================================
  // Every 1 000 ms: if anything is in the buffers, apply all queued updates
  // in a single pass and then wipe the buffers clean.
  // ==========================================================================
  useEffect(() => {
    const flushInterval = setInterval(() => {
      const updates = pendingUpdatesRef.current;
      const pulses  = pendingPulsesRef.current;

      // --- UPDATE: live anomalies (buffered) + expiry after LIVE_ALERT_TTL_MS
      const alerts = pendingAlertsRef.current;
      if (Object.keys(alerts).length) {
        pendingAlertsRef.current = {};
        setLiveAlerts((prev) => ({ ...prev, ...alerts }));
        Object.keys(alerts).forEach((id) => pulses.add(id));
      }
      setLiveAlerts((prev) => {
        const now = Date.now();
        const keys = Object.keys(prev);
        if (!keys.some((k) => now - prev[k].at > LIVE_ALERT_TTL_MS)) return prev;
        const next = {};
        keys.forEach((k) => { if (now - prev[k].at <= LIVE_ALERT_TTL_MS) next[k] = prev[k]; });
        return next;
      });
      // --- END UPDATE

      if (Object.keys(updates).length === 0 && pulses.size === 0) return;

      // 1. Batch-apply all asset field changes in one setState call
      setAssets((prev) =>
        prev.map((a) => (updates[a.id] || updates[String(a.id)] ? { ...a, ...(updates[a.id] || updates[String(a.id)]) } : a))
      );

      // 2. Patch the open Drawer if its asset was updated
      setSelectedAsset((prev) =>
        prev && (updates[prev.id] || updates[String(prev.id)]) ? { ...prev, ...(updates[prev.id] || updates[String(prev.id)]) } : prev
      );

      // 3. Show the telemetry pulse on the most recently updated sensor
      if (pulses.size > 0) {
        const pulseArray = Array.from(pulses);
        setPulsingAssetId(String(pulseArray[pulseArray.length - 1])); // --- UPDATE: string ids
        if (pulseTimerRef.current) clearTimeout(pulseTimerRef.current);
        pulseTimerRef.current = setTimeout(() => setPulsingAssetId(null), 1500);
      }

      // 4. Wipe the buffers clean for the next tick
      pendingUpdatesRef.current = {};
      pendingPulsesRef.current.clear();
    }, 1000);

    return () => clearInterval(flushInterval);
  }, []);

  // --------------------------------------------------------------------------
  // DERIVED DATA
  // --------------------------------------------------------------------------
  // --- UPDATE: assets + ML risk; brief for the Grid AI
  const enrichedAssets = useMemo(() => mergeRiskIntoAssets(assets, riskIndex), [assets, riskIndex]);
  useEffect(() => { assetsRef.current = assets; }, [assets]);
  useEffect(() => { selectedAssetIdRef.current = selectedAsset?.id ?? null; }, [selectedAsset?.id]);
  const gridBrief = useMemo(
    () => buildGridBrief(enrichedAssets, { zones: assignmentZones, liveAlerts, wsConnected: isConnected }),
    [enrichedAssets, assignmentZones, liveAlerts, isConnected]
  );
  const selectedEnriched = useMemo(() => {
    if (!selectedAsset) return null;
    const r = riskIndex[String(selectedAsset.id)];
    return r ? { ...selectedAsset, ...r, predicted_failure_date: selectedAsset.predicted_failure_date || r.risk_predicted_failure_date || null } : selectedAsset;
  }, [selectedAsset, riskIndex]);
  // --- END UPDATE
  const assetTypes = useMemo(() => {
    const types = [...new Set(assets.map((a) => a.type).filter(Boolean))];
    return types.sort();
  }, [assets]);

  const assetStatuses = useMemo(() => {
    const statuses = [...new Set(assets.map((a) => a.status).filter(Boolean))];
    return statuses.sort();
  }, [assets]);

  const filteredAssets = useMemo(() => {
    return enrichedAssets.filter((asset) => { // --- UPDATE: enriched with ML risk
      const matchesSearch =
        !searchTerm ||
        asset.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.location_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        asset.zone_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(asset.id).includes(searchTerm);

      const matchesType =
        typeFilter === 'all' || (asset.type || '').toLowerCase().includes(typeFilter.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' || (asset.status || '').toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [enrichedAssets, searchTerm, typeFilter, statusFilter]);

  // Reset to page 1 whenever search or filters change so the user never lands
  // on an empty page after narrowing results.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, typeFilter, statusFilter]);

  // Slice filteredAssets for the sidebar — the map still renders ALL assets.
  const paginatedAssets = useMemo(() => {
    const start = (currentPage - 1) * ASSETS_PER_PAGE;
    return filteredAssets.slice(start, start + ASSETS_PER_PAGE);
  }, [filteredAssets, currentPage]);

  const totalPages = Math.ceil(filteredAssets.length / ASSETS_PER_PAGE);

  const statusCounts = useMemo(() => {
    const counts = { operational: 0, warning: 0, critical: 0, offline: 0, maintenance: 0, other: 0 };
    assets.forEach((a) => {
      const s = (a.status || '').toLowerCase();
      if (s === 'operational' || s === 'active') counts.operational++;
      else if (s === 'warning') counts.warning++;
      else if (s === 'critical') counts.critical++;
      else if (s === 'offline' || s === 'inactive') counts.offline++;
      else if (s === 'maintenance') counts.maintenance++;
      else counts.other++;
    });
    return counts;
  }, [assets]);

  const typeCounts = useMemo(() => {
    const counts = {};
    assets.forEach((a) => {
      const t = a.type || 'Unknown';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [assets]);

  // AI Health distribution counts
  const healthCounts = useMemo(() => {
    const counts = { optimal: 0, warning: 0, critical: 0, unknown: 0 };
    assets.forEach((a) => {
      const score = a.health_score;
      if (score == null) counts.unknown++;
      else if (score >= 80) counts.optimal++;
      else if (score >= 40) counts.warning++;
      else counts.critical++;
    });
    return counts;
  }, [assets]);

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------

  // handleSelectAsset — selects the asset on the map, fetches telemetry history AND checks the
  // Work Order database in parallel so the Generate button locks instantly if
  // an open ticket already exists for this asset (persists across page reloads).
  const handleSelectAsset = useCallback(async (asset) => {
    setSelectedAsset(asset);
    setPopupKey(Date.now()); 
    setFlyTo([asset.latitude, asset.longitude]);
    
    setAssetHistory([]);
    setIsLoadingHistory(true);
    setActiveWorkOrder(null);

    try {
      // 1. Fetch Telemetry History
      const readingRes = await authenticatedFetch(`/readings/asset/${asset.id}?limit=24`).catch(() => null);
      if (readingRes) {
        setAssetHistory(Array.isArray(readingRes) ? readingRes.reverse() : []);
      } else {
        setAssetHistory([]);
      }

      // 2. Fetch the definitively correct Active Work Order using the new service
      const activeWO = await workOrderService.getLiveGridActiveWorkOrder(asset.id);
      
      if (activeWO) {
        setActiveWorkOrder(activeWO);
        // Optimistically update map UI status to maintenance
        setAssets((prev) =>
          prev.map((a) => (a.id === asset.id ? { ...a, status: 'maintenance' } : a))
        );
        setSelectedAsset((prev) => ({ ...prev, status: 'maintenance' }));
      }
      
    } catch (err) {
      console.error('LiveGrid: Failed to fetch asset context data', err);
    } finally {
      setIsLoadingHistory(false);
    }

    setTimeout(() => setFlyTo(null), 1500);
  }, []);

  // Fetch a lightweight active-WO summary whenever the selected asset changes.
  // This powers the "Current Work Order" widget inside the map Popup card,
  // independent of the full drawer WO check (which uses workOrderService).
  // --- UPDATE: keyed by the asset ID — live patches replace the selectedAsset object every
  // second, which used to re-fetch the work order on every telemetry tick.
  const selectedAssetId = selectedAsset?.id ?? null;
  useEffect(() => {
    const fetchActiveWOForPopup = async () => {
      if (selectedAssetId == null) {
        setActiveAssetWO(null);
        return;
      }
      
      setIsLoadingWO(true);
      try {
        // Use the new dedicated service function to guarantee accuracy
        const activeWO = await workOrderService.getLiveGridActiveWorkOrder(selectedAssetId);
        if (!isMountedRef.current || selectedAssetIdRef.current !== selectedAssetId) return; // stale
        setActiveAssetWO(activeWO);
      } catch (err) {
        console.error('LiveGrid: Failed to fetch popup WO for asset', err);
        setActiveAssetWO(null);
      } finally {
        setIsLoadingWO(false);
      }
    };
    
    fetchActiveWOForPopup();
  }, [selectedAssetId]);
  // --- END UPDATE

  // --- UPDATE: live work order events re-check the selected asset's active ticket
  const refreshSelectedWo = useCallback(async () => {
    const id = selectedAssetIdRef.current;
    if (id == null) return;
    try {
      const wo = await workOrderService.getLiveGridActiveWorkOrder(id);
      if (!isMountedRef.current || selectedAssetIdRef.current !== id) return;
      setActiveWorkOrder(wo || null);
      setActiveAssetWO(wo || null);
    } catch (e) {
      console.warn('LiveGrid: active work order refresh failed', e?.message || e);
    }
  }, []);
  useEffect(() => { refreshSelectedWoRef.current = refreshSelectedWo; }, [refreshSelectedWo]);

  /** Focus an asset from the Grid AI (clears filters that would hide it). */
  const handleFocusAsset = useCallback((asset) => {
    if (!asset) return;
    if (!filteredAssets.some((a) => a.id === asset.id)) {
      setSearchTerm('');
      setTypeFilter('all');
      setStatusFilter('all');
    }
    handleSelectAsset(asset);
  }, [filteredAssets, handleSelectAsset]);

  /** Typed work order draft for the selected asset (domain + type from the catalog, AI-assessed). */
  const buildAssetWorkOrderDraft = useCallback((description) => {
    const a = selectedEnriched || selectedAsset;
    if (!a) return null;
    const insight = assessAsset(a, Date.now(), liveAlerts[String(a.id)] || null);
    // eslint-disable-next-line no-unused-vars
    const { _entityName, ...typed } = buildDraft('prediction', {
      asset_id: a.id, asset_type: a.type, asset_name: a.name,
      risk_score: a.risk_score, predictedFailureDate: a.predicted_failure_date,
    });
    const allowed = typesFor(typed.domain) || [];
    const want = ['emergency', 'replacement', 'corrective', 'predictive'].includes(insight.suggestedType) ? insight.suggestedType : 'predictive';
    const type = allowed.includes(want) ? want : (allowed.includes('predictive') ? 'predictive' : (allowed[0] || 'predictive'));
    // Priority: the original health rule, raised to the AI assessment when that is higher
    let priority = 'medium';
    if (a.health_score != null) {
      if (a.health_score < 40) priority = 'critical';
      else if (a.health_score < 80) priority = 'high';
    }
    if ((PRIORITY_RANK[insight.suggestedPriority] || 0) > (PRIORITY_RANK[priority] || 0)) priority = insight.suggestedPriority;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const draft = normalizeDraft({
      ...typed,
      work_order_type: type,
      category: 'Predictive Maintenance',
      status: 'pending',
      priority,
      due_date: tomorrow.toISOString().split('T')[0],
      asset_id: a.id,
      source: 'prediction',
      latitude: a.latitude,
      longitude: a.longitude,
      location_description: a.location_description || a.zone_name || undefined,
      description: description || [
        `Raised from the Live Grid by FlowSense AI for ${a.name} (${a.type || 'asset'}).`,
        ...insight.reasons.map((r) => `- ${r}`),
        a.prescriptive_action?.action ? `ML prescription: ${a.prescriptive_action.action}${a.prescriptive_action.reason ? ` — ${a.prescriptive_action.reason}` : ''}` : null,
        `Recommended: ${insight.recommendation}`,
      ].filter(Boolean).join('\n'),
    });
    draft.title = `AI Dispatch: Inspect ${a.name}`;
    if (!draft.title) draft.title = suggestTitle(draft, a.name);
    return draft;
  }, [selectedAsset, selectedEnriched, liveAlerts]);

  const handleOpenInForm = useCallback(() => {
    const draft = buildAssetWorkOrderDraft();
    if (!draft) return;
    onDetailClose();
    openCreateWorkOrder(navigate, draft);
  }, [buildAssetWorkOrderDraft, navigate, onDetailClose]);
  // --- END UPDATE

  const handleClearFilters = useCallback(() => {
    setSearchTerm('');
    setTypeFilter('all');
    setStatusFilter('all');
  }, []);

  const handleManualRefresh = useCallback(() => {
    fetchAssets(true);
    toast({
      title: 'Refreshing Data',
      description: 'Fetching latest asset telemetry...',
      status: 'info',
      duration: 1500,
      isClosable: true,
    });
  }, [fetchAssets, toast]);

  // --------------------------------------------------------------------------
  // STEP 2 — ONE-CLICK AUTO-GENERATE WORK ORDER
  // Calls Ollama for the AI description, auto-calculates priority from health
  // score, then POSTs directly to the backend — no page navigation required.
  // The map and drawer stay open; the ticket is dispatched in the background.
  // --------------------------------------------------------------------------
  const handleGenerateWorkOrder = useCallback(async () => {
    if (!selectedAsset) return;

    setIsGeneratingTriage(true);

    toast({
      title: 'Auto-Generating Work Order...',
      description: 'Ollama is analyzing asset data and dispatching the ticket.',
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top-right',
    });

    try {
      // 1. Get the AI description from Ollama.
      //    Withhold currentReading if the asset has no telemetry capability.
      const triageData = {
        assetName: selectedAsset.name,
        type: selectedAsset.type,
        healthScore: selectedAsset.health_score,
        zone: selectedAsset.zone_name,
        currentReading: hasTelemetry ? selectedAsset.last_psi : null,
      };

      // --- UPDATE: an Ollama outage no longer blocks the work order — the FlowSense AI
      // assessment (reasons + ML prescription + recommendation) becomes the description.
      const aiResponse = await authenticatedFetch('/ai/asset-triage', {
        method: 'POST',
        body: JSON.stringify(triageData),
      }).catch((triageErr) => {
        console.warn('LiveGrid: Ollama triage unavailable, using FlowSense AI assessment', triageErr?.message || triageErr);
        return null;
      });

      const aiDescription = aiResponse?.description || undefined;
      // --- END UPDATE

      // 2–3. --- UPDATE: typed payload (domain + work_order_type from the catalog, AI-assessed
      // type/priority, location). Title / category / source / due date unchanged.
      const newWorkOrderPayload = buildAssetWorkOrderDraft(aiDescription);
      // --- END UPDATE

      // 4. POST directly to the backend — no navigation needed.
      const createdWO = await authenticatedFetch('/workorders', {
        method: 'POST',
        body: JSON.stringify(newWorkOrderPayload),
      });

      // 5. Lock the button instantly using the returned WO data so the user
      //    can't double-submit without waiting for the next drawer open.
      // --- UPDATE: accept { workOrder } | { data } | the row itself
      const createdRow = createdWO?.workOrder || createdWO?.data || (createdWO?.id ? createdWO : null);
      if (createdRow) {
        setActiveWorkOrder(createdRow);
      } else {
        setActiveWorkOrder(true); // Fallback lock — truthy but no ID yet
      }
      // Tell every other FlowSense page / tab (dashboard widgets, work orders) right away
      publishBus(BUS_EVENTS.WORK_ORDER_CHANGED, { id: createdRow?.id, action: 'created', asset_id: selectedAsset.id, source: 'live_grid' });
      // --- END UPDATE

      // 6. Tell the database to update the asset's status — best-effort,
      //    the work order was already created so we don't abort on failure.
      try {
        await authenticatedFetch(`/assets/${selectedAsset.id}`, {
          method: 'PUT',
          body: JSON.stringify({ status: 'maintenance' }),
        });
      } catch (assetErr) {
        console.warn('LiveGrid: Could not update asset status in DB; WO was still created.', assetErr);
      }

      publishBus(BUS_EVENTS.ASSET_CHANGED, { id: selectedAsset.id, status: 'maintenance', source: 'live_grid' }); // --- UPDATE

      // 7. Confirm — map and drawer remain open.
      toast({
        title: 'Work Order Created!',
        description: `Ticket ${createdRow?.id || ''} has been dispatched.`, // --- UPDATE
        status: 'success',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });

      // Force a strict DB sync via the Smart Diffing Engine.
      await fetchAssets(true);

      // 8. Trigger spatial analytics recalculation so the Zone Card's
      //    "Active Work Orders" count reflects the new ticket immediately.
      triggerZoneRecalculation();

    } catch (err) {
      console.error('LiveGrid: Failed to auto-generate work order', err);
      toast({
        title: 'Generation Failed',
        description: 'Failed to create work order. Check console for details.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setIsGeneratingTriage(false);
    }
  }, [selectedAsset, hasTelemetry, toast, fetchAssets, triggerZoneRecalculation, buildAssetWorkOrderDraft]);

  // --------------------------------------------------------------------------
  // STEP 3 — Ollama AI Triage: call the backend specialized route that takes
  // the asset data and streams back an AI-generated technical brief. This is
  // used as a standalone "get a quick brief now" action inside the drawer,
  // separate from the full Work Order flow. The brief is shown via toast so
  // the user can decide whether to proceed to the WO page.
  // --------------------------------------------------------------------------
  const handleOllamaTriage = useCallback(async () => {
    if (!selectedAsset) return;
    setIsGeneratingTriage(true);

    toast({
      title: 'AI Triage Running',
      description: `Analyzing ${selectedAsset.name} with Ollama...`,
      status: 'info',
      duration: 2500,
      isClosable: true,
    });

    try {
      const response = await authenticatedFetch('/ai/asset-triage', {
        method: 'POST',
        body: JSON.stringify({
          asset_id: selectedAsset.id,
          asset_name: selectedAsset.name,
          asset_type: selectedAsset.type,
          status: selectedAsset.status,
          health_score: selectedAsset.health_score,
          last_psi: selectedAsset.last_psi,
          predicted_failure_date: selectedAsset.predicted_failure_date,
          remaining_useful_life: selectedAsset.remaining_useful_life,
          zone_name: selectedAsset.zone_name,
          installation_date: selectedAsset.installation_date,
          last_maintenance_date: selectedAsset.last_maintenance_date,
        }),
      });

      // The backend route returns { brief: string } — surface it to the user
      const brief = response?.brief || 'No triage brief returned.';
      toast({
        title: `AI Brief — ${selectedAsset.name}`,
        description: brief,
        status: 'success',
        duration: 12000,
        isClosable: true,
      });
    } catch (err) {
      console.error('LiveGrid: Ollama triage failed', err);
      toast({
        title: 'Triage Failed',
        description: 'Could not reach the AI triage service. Check your backend connection.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsGeneratingTriage(false);
    }
  }, [selectedAsset, toast]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (typeFilter !== 'all') count++;
    if (statusFilter !== 'all') count++;
    return count;
  }, [searchTerm, typeFilter, statusFilter]);

  const activeTile = TILE_LAYERS[tileLayer] || TILE_LAYERS.carto_voyager;
  // --- UPDATE: keyed CARTO URL unless it failed → public endpoint for the same style
  const activeTileUrl = tileUrlFor(TILE_LAYERS[tileLayer] ? tileLayer : 'carto_voyager', { useFallback: !!tileFallback[tileLayer] });
  const tileEventHandlers = useMemo(() => ({
    load: () => {
      tileStatsRef.current.loaded = true;
      setTileLoaded(true);
      setTileError(null);
    },
    tileload: () => { tileStatsRef.current.loaded = true; },
    tileerror: () => {
      const st = tileStatsRef.current;
      st.errors += 1;
      if (!st.loaded && st.errors >= TILE_FAILURE_THRESHOLD) {
        const layer = TILE_LAYERS[tileLayer];
        if (layer?.fallbackUrl && !tileFallback[tileLayer]) {
          st.errors = 0;
          setTileFallback((prev) => ({ ...prev, [tileLayer]: true }));
          setTileError('CARTO key rejected — using the public CARTO endpoint');
        } else {
          setTileError('Failed to load tiles');
        }
      }
    },
  }), [tileLayer, tileFallback]);
  // New basemap → reset the tile health counters (and any previous error)
  useEffect(() => {
    tileStatsRef.current = { errors: 0, loaded: false };
    setTileLoaded(false);
    setTileError(null);
  }, [tileLayer]);
  // Switched to the keyless fallback URL → reset counters, keep the fallback notice
  useEffect(() => {
    tileStatsRef.current = { errors: 0, loaded: false };
    setTileLoaded(false);
  }, [activeTileUrl]);
  // --- END UPDATE

  // --------------------------------------------------------------------------
  // LOADING STATE
  // --------------------------------------------------------------------------
  if (isLoading) {
    return (
      <Flex h="85vh" align="center" justify="center" direction="column" gap={4}>
        <MotionBox
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
        >
          <Flex
            w="80px"
            h="80px"
            bgGradient={panelGradient}
            borderRadius="2xl"
            align="center"
            justify="center"
            boxShadow="xl"
          >
            <Spinner size="lg" color="white" thickness="3px" speed="0.8s" />
          </Flex>
        </MotionBox>
        <Heading size="md" color={headingColor}>
          Initializing Command Center
        </Heading>
        <Text color={mutedColor} fontSize="sm">
          Loading asset telemetry and geospatial data...
        </Text>
      </Flex>
    );
  }

  // --------------------------------------------------------------------------
  // MAIN RENDER
  // --------------------------------------------------------------------------
  return (
    <Box minH="85vh">
      {/* ================================================================== */}
      {/* TOP BAR                                                            */}
      {/* ================================================================== */}
      <MotionBox
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          {/* Left: Title */}
          <Box>
            <HStack spacing={3}>
              <Flex
                w="42px"
                h="42px"
                bgGradient={aiMode ? aiGradient : panelGradient}
                borderRadius="xl"
                align="center"
                justify="center"
                boxShadow="md"
                transition="all 0.3s"
              >
                <Icon as={aiMode ? FaBrain : MdGpsFixed} color="white" boxSize={5} />
              </Flex>
              <Box>
                <HStack spacing={2}>
                  <Heading size="md" color={headingColor} lineHeight="1.2">
                    Command Center: Live Grid
                  </Heading>
                  {aiMode && (
                    <Badge
                      colorScheme="purple"
                      variant="solid"
                      borderRadius="full"
                      px={2}
                      fontSize="2xs"
                      textTransform="uppercase"
                    >
                      AI Lens Active
                    </Badge>
                  )}
                </HStack>
                <HStack spacing={2} mt={0.5}>
                  <Text
                    fontSize="xs"
                    color={isConnected ? 'green.500' : 'orange.500'}
                    fontWeight="bold"
                  >
                    {isConnected ? '● LIVE' : '○ RECONNECTING'}
                  </Text>
                  <Text color={mutedColor} fontSize="xs">
                    • {filteredAssets.length} Assets Visualized
                  </Text>
                  {lastRefreshTime && (
                    <Text color={mutedColor} fontSize="xs">
                      • Updated {lastRefreshTime.toLocaleTimeString()}
                    </Text>
                  )}
                  {isRefreshing && <Spinner size="xs" color="blue.400" />}
                </HStack>
              </Box>
            </HStack>
          </Box>

          {/* Right: Controls */}
          <HStack spacing={2} flexWrap="wrap">
            {/* --- AI LENS: Health distribution badges (shown when AI mode is on) --- */}
            <AnimatePresence>
              {aiMode && (
                <MotionBox
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <HStack spacing={1.5} display={{ base: 'none', lg: 'flex' }}>
                    <Badge colorScheme="green" px={2} py={1} borderRadius="md" fontSize="xs">
                      <HStack spacing={1}>
                        <Icon as={FaCheckCircle} boxSize={2.5} />
                        <Text>{healthCounts.optimal} Optimal</Text>
                      </HStack>
                    </Badge>
                    {healthCounts.warning > 0 && (
                      <Badge colorScheme="orange" px={2} py={1} borderRadius="md" fontSize="xs">
                        <HStack spacing={1}>
                          <Icon as={FaExclamationTriangle} boxSize={2.5} />
                          <Text>{healthCounts.warning} Deteriorating</Text>
                        </HStack>
                      </Badge>
                    )}
                    {healthCounts.critical > 0 && (
                      <Badge colorScheme="red" px={2} py={1} borderRadius="md" fontSize="xs">
                        <HStack spacing={1}>
                          <Icon as={FaTimesCircle} boxSize={2.5} />
                          <Text>{healthCounts.critical} At Risk</Text>
                        </HStack>
                      </Badge>
                    )}
                  </HStack>
                </MotionBox>
              )}
            </AnimatePresence>

            {/* Status badges (shown when AI mode is off) */}
            {!aiMode && (
              <HStack spacing={1.5} display={{ base: 'none', lg: 'flex' }}>
                <Badge colorScheme="green" px={2} py={1} borderRadius="md" fontSize="xs">
                  <HStack spacing={1}>
                    <Icon as={FaCheckCircle} boxSize={2.5} />
                    <Text>{statusCounts.operational} Online</Text>
                  </HStack>
                </Badge>
                {statusCounts.warning > 0 && (
                  <Badge colorScheme="orange" px={2} py={1} borderRadius="md" fontSize="xs">
                    <HStack spacing={1}>
                      <Icon as={FaExclamationTriangle} boxSize={2.5} />
                      <Text>{statusCounts.warning} Warning</Text>
                    </HStack>
                  </Badge>
                )}
                {statusCounts.critical > 0 && (
                  <Badge colorScheme="red" px={2} py={1} borderRadius="md" fontSize="xs">
                    <HStack spacing={1}>
                      <Icon as={FaTimesCircle} boxSize={2.5} />
                      <Text>{statusCounts.critical} Critical</Text>
                    </HStack>
                  </Badge>
                )}
              </HStack>
            )}

            <Divider orientation="vertical" h="24px" display={{ base: 'none', lg: 'block' }} />

            {/* --- UPDATE: FlowSense AI for the grid + guide + live events --- */}
            <Tooltip hasArrow label={gridBrief.deductions.length ? `Grid score — ${gridBrief.deductions.map((d) => `−${d.points} ${d.why}`).join(' · ')}` : 'Grid score — no deductions'}>
              <Badge colorScheme={gridBrief.grade.color} px={2} py={1} borderRadius="md" fontSize="xs" cursor="help" aria-label={`Grid score ${gridBrief.score}`}>
                Grid {gridBrief.score}
              </Badge>
            </Tooltip>
            {systemBrief && (
              <Tooltip hasArrow label={`FlowSense system: ${systemBrief.headline}`}>
                <Badge colorScheme={systemBrief.grade?.color || 'gray'} variant="outline" px={2} py={1} borderRadius="md" fontSize="xs" cursor="help">
                  System {systemBrief.score}
                </Badge>
              </Tooltip>
            )}
            {feed?.total > 0 && (
              <Tooltip hasArrow label="Live events received on this page (WebSocket + cross-page updates)">
                <Badge colorScheme="green" variant="subtle" px={2} py={1} borderRadius="md" fontSize="xs">{feed.total} live</Badge>
              </Tooltip>
            )}
            <Button size="sm" colorScheme="purple" borderRadius="lg" leftIcon={<Icon as={MdAutoAwesome} />} onClick={onGridAiOpen} aria-label="Open Grid AI">
              Grid AI
            </Button>
            <Tooltip label="FlowSense guide" hasArrow>
              <IconButton icon={<Icon as={MdHelpOutline} />} size="sm" variant="ghost" borderRadius="lg" onClick={onGuideOpen} aria-label="Open FlowSense guide" />
            </Tooltip>
            {/* --- END UPDATE --- */}

            {/* --- PHASE 5: ZONE BOUNDARY TOGGLE --- */}
            <Tooltip label={showZones ? 'Hide District Boundaries' : 'Show District Boundaries'} hasArrow>
              <IconButton
                icon={<Icon as={FaMap} />}
                size="sm"
                onClick={() => setShowZones(!showZones)}
                variant={showZones ? 'solid' : 'outline'}
                colorScheme={showZones ? 'blue' : 'gray'}
                borderRadius="lg"
                aria-label="Toggle zone boundaries"
              />
            </Tooltip>

            {/* --- AI LENS TOGGLE --- */}
            <Tooltip label="Toggle AI Predictive Health Overlay" hasArrow>
              <HStack
                bg={aiMode ? aiModeBg : 'transparent'}
                px={3}
                py={1}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={aiMode ? aiModeBorder : borderColor}
                transition="all 0.2s"
                spacing={2}
              >
                <Icon
                  as={FaSatelliteDish}
                  color={aiMode ? 'purple.500' : mutedColor}
                  boxSize={3.5}
                />
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel htmlFor="ai-mode" mb={0} fontSize="xs" fontWeight="700" mr={1.5}>
                    AI Lens
                  </FormLabel>
                  <Switch
                    id="ai-mode"
                    size="sm"
                    colorScheme="purple"
                    isChecked={aiMode}
                    onChange={(e) => setAiMode(e.target.checked)}
                  />
                </FormControl>
              </HStack>
            </Tooltip>

            {/* Auto-refresh */}
            <Tooltip label={autoRefresh ? 'Auto-refresh ON (30s)' : 'Auto-refresh OFF'} hasArrow>
              <Box
                bg={autoRefresh ? 'green.50' : 'transparent'}
                px={2}
                py={1}
                borderRadius="lg"
                borderWidth="1px"
                borderColor={autoRefresh ? 'green.300' : borderColor}
              >
                <FormControl display="flex" alignItems="center" w="auto">
                  <FormLabel htmlFor="grid-auto-refresh" mb={0} fontSize="xs" mr={1.5} fontWeight="600">
                    Live
                  </FormLabel>
                  <Switch
                    id="grid-auto-refresh"
                    size="sm"
                    isChecked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    colorScheme="green"
                  />
                </FormControl>
              </Box>
            </Tooltip>

            <Tooltip label="Refresh now" hasArrow>
              <IconButton
                icon={<Icon as={MdRefresh} />}
                size="sm"
                onClick={handleManualRefresh}
                variant="outline"
                borderRadius="lg"
                aria-label="Refresh"
                isLoading={isRefreshing}
              />
            </Tooltip>

            <Tooltip label={showSidebar ? 'Hide panel' : 'Show panel'} hasArrow>
              <IconButton
                icon={<Icon as={ViewIcon} />}
                size="sm"
                onClick={() => setShowSidebar(!showSidebar)}
                variant={showSidebar ? 'solid' : 'outline'}
                colorScheme={showSidebar ? 'blue' : 'gray'}
                borderRadius="lg"
                aria-label="Toggle sidebar"
              />
            </Tooltip>

            <Menu>
              <Tooltip label="Map style" hasArrow>
                <MenuButton
                  as={IconButton}
                  icon={<Icon as={FaLayerGroup} />}
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  aria-label="Map layers"
                />
              </Tooltip>
              {/* --- UPDATE: grouped basemaps (CARTO via the FlowSense API key, imagery, streets & terrain) --- */}
              <MenuList borderRadius="xl" boxShadow="xl" minW="240px" maxH="70vh" overflowY="auto" zIndex={1500}>
                {TILE_GROUPS.map((group, gi) => (
                  <React.Fragment key={group}>
                    {gi > 0 && <MenuDivider />}
                    <MenuGroup title={group} fontSize="xs">
                      {Object.entries(TILE_LAYERS).filter(([, l]) => l.group === group).map(([key, layer]) => (
                        <MenuItem
                          key={key}
                          onClick={() => setTileLayer(key)}
                          fontWeight={tileLayer === key ? '700' : '400'}
                          borderRadius="lg"
                          fontSize="sm"
                          title={layer.description}
                          aria-checked={tileLayer === key}
                          role="menuitemradio"
                        >
                          <Box flex="1">
                            <Text>{layer.label}</Text>
                            <Text fontSize="2xs" color={mutedColor} noOfLines={1}>{layer.description}</Text>
                          </Box>
                          {tileLayer === key && (
                            <Badge ml={2} colorScheme={tileFallback[key] ? 'orange' : 'blue'} fontSize="2xs">{tileFallback[key] ? 'Fallback' : 'Active'}</Badge>
                          )}
                        </MenuItem>
                      ))}
                    </MenuGroup>
                  </React.Fragment>
                ))}
              </MenuList>
              {/* --- END UPDATE --- */}
            </Menu>

            <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen map'} hasArrow>
              <IconButton
                icon={<Icon as={isFullscreen ? FaCompress : FaExpand} />}
                size="sm"
                onClick={() => setIsFullscreen(!isFullscreen)}
                variant="outline"
                borderRadius="lg"
                aria-label="Toggle fullscreen"
              />
            </Tooltip>

            <Badge colorScheme="blue" px={3} py={1.5} borderRadius="lg" fontSize="xs" fontWeight="700">
              {filteredAssets.length}/{assets.length} Assets
            </Badge>
          </HStack>
        </Flex>
      </MotionBox>

      {/* ================================================================== */}
      {/* AI MODE BANNER — contextual explanation when AI Lens is active     */}
      {/* ================================================================== */}
      <AnimatePresence>
        {aiMode && (
          <MotionBox
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            mb={3}
          >
            <GlassPanel
              px={4}
              py={3}
              bgGradient="linear(to-r, purple.50, pink.50)"
              borderColor="purple.200"
            >
              <Flex align="center" gap={3} flexWrap="wrap">
                <Icon as={FaBrain} color="purple.500" boxSize={5} />
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="700" color="purple.700">
                    AI Predictive Health Overlay Active
                  </Text>
                  <Text fontSize="xs" color="purple.600">
                    Markers now show ML-predicted health scores instead of asset type. Green = Optimal (80-100%), Orange = Deteriorating (40-79%), Red = Critical Risk (0-39%).
                  </Text>
                </Box>
                <HStack spacing={3} display={{ base: 'none', md: 'flex' }}>
                  <HStack spacing={1.5}>
                    <Box w="12px" h="12px" borderRadius="full" bg={AI_HEALTH_COLORS.optimal} />
                    <Text fontSize="xs" color="purple.600" fontWeight="500">80-100%</Text>
                  </HStack>
                  <HStack spacing={1.5}>
                    <Box w="12px" h="12px" borderRadius="full" bg={AI_HEALTH_COLORS.warning} />
                    <Text fontSize="xs" color="purple.600" fontWeight="500">40-79%</Text>
                  </HStack>
                  <HStack spacing={1.5}>
                    <Box w="12px" h="12px" borderRadius="full" bg={AI_HEALTH_COLORS.critical} />
                    <Text fontSize="xs" color="purple.600" fontWeight="500">0-39%</Text>
                  </HStack>
                </HStack>
              </Flex>
            </GlassPanel>
          </MotionBox>
        )}
      </AnimatePresence>

      {/* ================================================================== */}
      {/* MAIN CONTENT: Sidebar + Map                                        */}
      {/* ================================================================== */}
      <MotionBox
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <Flex
          gap={4}
          h={isFullscreen ? 'calc(100vh - 60px)' : 'calc(100vh - 240px)'}
          minH="550px"
          direction={{ base: 'column', lg: 'row' }}
        >
          {/* ============================================================ */}
          {/* LEFT SIDEBAR                                                 */}
          {/* ============================================================ */}
          <AnimatePresence>
            {showSidebar && (
              <MotionBox
                initial={{ opacity: 0, x: -20, width: 0 }}
                animate={{ opacity: 1, x: 0, width: 'auto' }}
                exit={{ opacity: 0, x: -20, width: 0 }}
                transition={{ duration: 0.3 }}
                w={{ base: '100%', lg: '340px' }}
                minW={{ lg: '340px' }}
                flexShrink={0}
              >
                <GlassPanel h="100%" display="flex" flexDirection="column" overflow="hidden">
                  {/* Search */}
                  <Box p={3} pb={2}>
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <Icon as={SearchIcon} color="gray.400" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search assets..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        borderRadius="lg"
                        bg={inputBg}
                        fontSize="sm"
                        _focus={{
                          borderColor: 'blue.400',
                          boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                        }}
                      />
                      {searchTerm && (
                        <Box position="absolute" right={2} top="50%" transform="translateY(-50%)" zIndex={2}>
                          <IconButton
                            icon={<CloseIcon />}
                            size="xs"
                            variant="ghost"
                            onClick={() => setSearchTerm('')}
                            aria-label="Clear search"
                          />
                        </Box>
                      )}
                    </InputGroup>

                    <Flex mt={2} justify="space-between" align="center">
                      <Button
                        size="xs"
                        variant="ghost"
                        leftIcon={<Icon as={FaFilter} />}
                        onClick={() => setShowFilters(!showFilters)}
                        color={mutedColor}
                        fontWeight="500"
                        rightIcon={<Icon as={showFilters ? ChevronUpIcon : ChevronDownIcon} boxSize={3} />}
                      >
                        Filters
                        {activeFilterCount > 0 && (
                          <Badge ml={1} colorScheme="blue" borderRadius="full" fontSize="2xs">
                            {activeFilterCount}
                          </Badge>
                        )}
                      </Button>
                      {activeFilterCount > 0 && (
                        <Button size="xs" variant="ghost" colorScheme="red" onClick={handleClearFilters}>
                          Clear
                        </Button>
                      )}
                    </Flex>

                    <Collapse in={showFilters} animateOpacity>
                      <VStack spacing={2} mt={2}>
                        <Select
                          size="xs"
                          borderRadius="lg"
                          value={typeFilter}
                          onChange={(e) => setTypeFilter(e.target.value)}
                          bg={inputBg}
                        >
                          <option value="all">All Types</option>
                          {assetTypes.map((t) => (
                            <option key={t} value={t}>{t} ({typeCounts[t] || 0})</option>
                          ))}
                        </Select>
                        <Select
                          size="xs"
                          borderRadius="lg"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          bg={inputBg}
                        >
                          <option value="all">All Statuses</option>
                          {assetStatuses.map((s) => (
                            <option key={s} value={s}>{s} ({assets.filter((a) => a.status === s).length})</option>
                          ))}
                        </Select>
                      </VStack>
                    </Collapse>

                    {activeFilterCount > 0 && (
                      <Wrap spacing={1} mt={2}>
                        {searchTerm && (
                          <WrapItem>
                            <Tag size="sm" borderRadius="full" colorScheme="blue" variant="subtle">
                              <TagLabel fontSize="2xs">"{searchTerm}"</TagLabel>
                              <TagCloseButton onClick={() => setSearchTerm('')} />
                            </Tag>
                          </WrapItem>
                        )}
                        {typeFilter !== 'all' && (
                          <WrapItem>
                            <Tag size="sm" borderRadius="full" colorScheme="purple" variant="subtle">
                              <TagLabel fontSize="2xs">{typeFilter}</TagLabel>
                              <TagCloseButton onClick={() => setTypeFilter('all')} />
                            </Tag>
                          </WrapItem>
                        )}
                        {statusFilter !== 'all' && (
                          <WrapItem>
                            <Tag size="sm" borderRadius="full" colorScheme="green" variant="subtle">
                              <TagLabel fontSize="2xs">{statusFilter}</TagLabel>
                              <TagCloseButton onClick={() => setStatusFilter('all')} />
                            </Tag>
                          </WrapItem>
                        )}
                      </Wrap>
                    )}
                  </Box>

                  <Divider />

                  {/* Quick stats — switches between status and health depending on AI mode */}
                  <SimpleGrid columns={3} gap={2} p={3} pb={2}>
                    {aiMode ? (
                      <>
                        <Box bg={statBg} p={2} borderRadius="lg" textAlign="center">
                          <Text fontSize="lg" fontWeight="700" color="green.400">{healthCounts.optimal}</Text>
                          <Text fontSize="2xs" color={mutedColor} fontWeight="500">Optimal</Text>
                        </Box>
                        <Box bg={statBg} p={2} borderRadius="lg" textAlign="center">
                          <Text fontSize="lg" fontWeight="700" color="orange.400">{healthCounts.warning}</Text>
                          <Text fontSize="2xs" color={mutedColor} fontWeight="500">Deteriorating</Text>
                        </Box>
                        <Box bg={statBg} p={2} borderRadius="lg" textAlign="center">
                          <Text fontSize="lg" fontWeight="700" color="red.400">{healthCounts.critical}</Text>
                          <Text fontSize="2xs" color={mutedColor} fontWeight="500">At Risk</Text>
                        </Box>
                      </>
                    ) : (
                      <>
                        <Box bg={statBg} p={2} borderRadius="lg" textAlign="center">
                          <Text fontSize="lg" fontWeight="700" color="green.400">{statusCounts.operational}</Text>
                          <Text fontSize="2xs" color={mutedColor} fontWeight="500">Online</Text>
                        </Box>
                        <Box bg={statBg} p={2} borderRadius="lg" textAlign="center">
                          <Text fontSize="lg" fontWeight="700" color="orange.400">{statusCounts.warning + statusCounts.maintenance}</Text>
                          <Text fontSize="2xs" color={mutedColor} fontWeight="500">Alerts</Text>
                        </Box>
                        <Box bg={statBg} p={2} borderRadius="lg" textAlign="center">
                          <Text fontSize="lg" fontWeight="700" color="red.400">{statusCounts.critical + statusCounts.offline}</Text>
                          <Text fontSize="2xs" color={mutedColor} fontWeight="500">Issues</Text>
                        </Box>
                      </>
                    )}
                  </SimpleGrid>

                  <Divider />

                  <Flex px={3} py={2} justify="space-between" align="center">
                    <Text fontSize="xs" fontWeight="600" color={mutedColor} textTransform="uppercase" letterSpacing="wide">
                      Assets ({filteredAssets.length})
                    </Text>
                  </Flex>

                  <Box
                    flex="1"
                    overflowY="auto"
                    px={1.5}
                    pb={2}
                    css={{
                      '&::-webkit-scrollbar': { width: '6px' },
                      '&::-webkit-scrollbar-track': { background: scrollTrackBg },
                      '&::-webkit-scrollbar-thumb': { background: scrollThumbBg, borderRadius: '3px' },
                    }}
                  >
                    {filteredAssets.length === 0 ? (
                      <Flex direction="column" align="center" justify="center" h="200px" gap={2}>
                        <Icon as={SearchIcon} boxSize={8} color="gray.300" />
                        <Text fontSize="sm" color={mutedColor} textAlign="center">No assets match your filters</Text>
                        <Button size="xs" variant="ghost" colorScheme="blue" onClick={handleClearFilters}>Clear filters</Button>
                      </Flex>
                    ) : (
                      paginatedAssets.map((asset) => (
                        <AssetListItem
                          key={asset.id}
                          asset={asset}
                          isSelected={selectedAsset?.id === asset.id}
                          onSelect={handleSelectAsset}
                          aiMode={aiMode}
                          liveAlert={liveAlerts[String(asset.id)]}
                        />
                      ))
                    )}
                  </Box>

                  {/* Pagination controls — only shown when there are multiple pages */}
                  {totalPages > 1 && (
                    <Flex
                      justify="space-between"
                      align="center"
                      p={3}
                      borderTopWidth="1px"
                      borderColor={borderColor}
                      bg={cardBg}
                      flexShrink={0}
                    >
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        isDisabled={currentPage === 1}
                      >
                        Prev
                      </Button>
                      <Text fontSize="xs" color={mutedColor} fontWeight="600">
                        Page {currentPage} of {totalPages}
                      </Text>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        isDisabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </Flex>
                  )}
                </GlassPanel>
              </MotionBox>
            )}
          </AnimatePresence>

          {/* ============================================================ */}
          {/* MAP PANE                                                     */}
          {/* ============================================================ */}
          <Box
            flex="1"
            borderRadius="xl"
            overflow="hidden"
            borderWidth="2px"
            borderColor={aiMode ? 'purple.300' : borderColor}
            boxShadow={aiMode ? '0 0 20px rgba(128,90,213,0.15)' : 'lg'}
            position="relative"
            bg={cardBg}
            transition="all 0.3s"
          >
            {isRefreshing && (
              <Box position="absolute" top={0} left={0} right={0} zIndex={500}>
                <Progress size="xs" isIndeterminate colorScheme={aiMode ? 'purple' : 'blue'} />
              </Box>
            )}

            <MapContainer
              preferCanvas={true}
              center={DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              style={{ height: '100%', width: '100%', zIndex: 1 }}
              zoomControl={false}
            >
              <MapController flyTo={flyTo} />
              <ZoomControl position="bottomright" />
              {/* --- UPDATE: basemap with load / error tracking (+ automatic CARTO fallback) and overlays */}
              <TileLayer
                key={`${tileLayer}-${tileFallback[tileLayer] ? 'fb' : 'key'}`}
                url={activeTileUrl}
                attribution={activeTile.attribution}
                maxZoom={activeTile.maxZoom || 19}
                detectRetina={false}
                eventHandlers={tileEventHandlers}
              />
              {(activeTile.overlays || []).map((ov, i) => (
                <TileLayer key={`${tileLayer}-ov-${i}`} url={ov.url} opacity={ov.opacity ?? 1} maxZoom={activeTile.maxZoom || 19} zIndex={2 + i} />
              ))}
              {/* --- END UPDATE */}

              {/* ============================================================ */}
              {/* PHASE 5: GEOSPATIAL ZONE BOUNDARIES                         */}
              {/* Rendered below markers so zone polygons never block clicks.  */}
              {/* ============================================================ */}
              {showZones && assignmentZones.map((zone) => {
                if (!zone.parsedGeometry) return null;

                const feature = {
                  type: 'Feature',
                  properties: {
                    id:                   zone.id,
                    name:                 zone.name || 'Zone',
                    description:          zone.description || '',
                    total_assets:         zone.total_assets,
                    active_work_orders:   zone.active_work_orders,
                    average_health_score: zone.average_health_score,
                    asset_breakdown:      zone.asset_breakdown,
                  },
                  geometry: zone.parsedGeometry,
                };

                const isSelected = selectedZone?.id === zone.id;

                return (
                  <GeoJSON
                    key={`zone-${zone.id}`}
                    data={feature}
                    style={{
                      color:       isSelected ? '#D6BCFA' : '#805AD5',
                      weight:      isSelected ? 4 : 2,
                      opacity:     0.8,
                      fillColor:   '#B794F4',
                      fillOpacity: isSelected ? 0.30 : 0.15,
                      dashArray:   isSelected ? '' : '5, 5',
                    }}
                    onEachFeature={(feat, layer) => {
                      layer.bindTooltip(
                        `<div style="text-align:center;"><strong>${feat.properties.name}</strong></div>`,
                        { sticky: true, className: 'custom-zone-tooltip' }
                      );
                      layer.on({
                        mouseover: (e) => e.target.setStyle({ fillOpacity: 0.30, weight: 3 }),
                        mouseout:  (e) => e.target.setStyle({
                          fillOpacity: selectedZone?.id === zone.id ? 0.30 : 0.15,
                          weight:      selectedZone?.id === zone.id ? 4 : 2,
                        }),
                        click: () => setSelectedZone(feat.properties),
                      });
                    }}
                  />
                );
              })}

              {/* RAW INFRASTRUCTURE: Pipelines & IoT Sensors */}
              <SensorMapLayer sensors={rawSensors} pipelines={pipelines} />

              {/* ============================================================ */}
              {/* CLUSTERED ASSET MARKERS                                      */}
              {/* MarkerClusterGroup combines nearby markers into one numbered  */}
              {/* circle when zoomed out, exploding back on zoom-in.           */}
              {/* chunkedLoading renders clusters in batches so the UI never   */}
              {/* freezes on large datasets (100+ assets).                     */}
              {/* ============================================================ */}
              <MarkerClusterGroup chunkedLoading={true} maxClusterRadius={60}>
                {filteredAssets.map((asset) => {
                  const markerIcon = getDynamicIcon(asset);
                  return (
                    <Marker
                      key={`marker-${asset.id}`}
                      position={[asset.latitude, asset.longitude]}
                      icon={markerIcon}
                      eventHandlers={{ click: () => handleSelectAsset(asset) }}
                    >
                    </Marker>
                  );
                })}
              </MarkerClusterGroup>

              {/* ── STANDALONE DECOUPLED POPUP CARD ── */}
              <AssetPopupCard
                popupKey={popupKey}
                asset={selectedAsset}
                aiMode={aiMode}
                activeAssetWO={activeAssetWO}
                isLoadingWO={isLoadingWO}
                onDetailOpen={onDetailOpen}
                getAssetColor={getAssetColor}
                getAssetReactIcon={getAssetReactIcon}
                getStatusConfig={getStatusConfig}
                getHealthColorScheme={getHealthColorScheme}
              />

              {/* ============================================================ */}
              {/* UNCLUSTERED DYNAMIC OVERLAYS                                 */}
              {/* Telemetry pulses and AI health halos are CircleMarkers that  */}
              {/* must live OUTSIDE MarkerClusterGroup — they follow the actual */}
              {/* asset position and don't participate in clustering math.     */}
              {/* Only renders for assets that actually need an overlay, so    */}
              {/* the loop short-circuits on null for the majority of assets.  */}
              {/* ============================================================ */}
              {filteredAssets.map((asset) => {
                const markerColor = getDynamicColor(asset);
                const isPulsing = pulsingAssetId != null && String(pulsingAssetId) === String(asset.id); // --- UPDATE: string ids
                const showHalo = aiMode && asset.health_score != null && asset.health_score < 80;
                // --- UPDATE: live anomaly pulse (WebSocket) + ML failure-risk ring
                const liveAlert = liveAlerts[String(asset.id)];
                const showRisk = asset.risk_score != null && asset.risk_score >= 70;

                if (!isPulsing && !showHalo && !liveAlert && !showRisk) return null;
                // --- END UPDATE

                return (
                  <React.Fragment key={`overlay-${asset.id}`}>
                    {isPulsing && (
                      <CircleMarker
                        center={[asset.latitude, asset.longitude]}
                        radius={25}
                        pathOptions={{ color: '#3182CE', fillColor: '#3182CE', fillOpacity: 0.4, weight: 0 }}
                        className="telemetry-pulse"
                      />
                    )}
                    {/* --- UPDATE */}
                    {liveAlert && (
                      <CircleMarker
                        center={[asset.latitude, asset.longitude]}
                        radius={liveAlert.severity === 'critical' ? 30 : 24}
                        pathOptions={{ color: '#E53E3E', fillColor: '#E53E3E', fillOpacity: 0.25, weight: 2 }}
                        className="live-alert-pulse"
                      />
                    )}
                    {showRisk && (
                      <CircleMarker
                        center={[asset.latitude, asset.longitude]}
                        radius={22}
                        pathOptions={{ color: '#805AD5', fillOpacity: 0, weight: 2, dashArray: '4 4' }}
                      />
                    )}
                    {/* --- END UPDATE */}
                    {showHalo && (
                      <CircleMarker
                        center={[asset.latitude, asset.longitude]}
                        radius={18}
                        pathOptions={{
                          color: markerColor,
                          fillColor: markerColor,
                          fillOpacity: 0.12,
                          weight: 1.5,
                          dashArray: asset.health_score < 40 ? '' : '5 3',
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}

              {/* Selected-asset selection ring — outside cluster so it always shows */}
              {selectedAsset && (
                <CircleMarker
                  center={[selectedAsset.latitude, selectedAsset.longitude]}
                  radius={20}
                  pathOptions={{
                    color: getDynamicColor(selectedAsset),
                    fillColor: getDynamicColor(selectedAsset),
                    fillOpacity: 0.15,
                    weight: 2,
                    dashArray: '6 4',
                  }}
                />
              )}
            </MapContainer>

            {/* --- UPDATE: Grid AI strip + basemap status --- */}
            <Box position="absolute" top={3} left={3} zIndex={450} display={{ base: 'none', md: 'block' }}>
              <GridAiStrip brief={gridBrief} onOpen={onGridAiOpen} onFocusAsset={handleFocusAsset} />
            </Box>
            {(!tileLoaded || tileError) && (
              <Box position="absolute" bottom={4} right={14} zIndex={450}>
                <Tooltip hasArrow label={tileError || 'Loading basemap tiles…'}>
                  <Badge colorScheme={tileError ? 'orange' : 'gray'} variant="solid" borderRadius="md" px={2} py={1} fontSize="2xs" aria-live="polite">
                    {tileError ? (tileFallback[tileLayer] ? 'Basemap: public fallback' : 'Basemap error') : `Loading ${activeTile.label}…`}
                  </Badge>
                </Tooltip>
              </Box>
            )}
            {/* --- END UPDATE --- */}

            {/* ================================================================ */}
            {/* PHASE 5: FLOATING ZONE ANALYTICS CARD                            */}
            {/* Slides in from the right when an admin clicks a zone polygon.   */}
            {/* Auto-dismisses when zones are hidden or the X button is clicked. */}
            {/* ================================================================ */}
            <AnimatePresence>
              {selectedZone && showZones && (
                <MotionBox
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 50 }}
                  transition={{ duration: 0.25 }}
                  position="absolute"
                  top={4}
                  right={4}
                  zIndex={1000}
                  w="300px"
                >
                  <GlassPanel p={5}>
                    {/* Header */}
                    <Flex justify="space-between" align="flex-start" mb={3}>
                      <HStack spacing={2}>
                        <Icon as={FaMap} color="purple.500" boxSize={4} />
                        <Heading size="sm" color={headingColor} noOfLines={1}>
                          {selectedZone.name}
                        </Heading>
                      </HStack>
                      <HStack spacing={1}>
                        <Tooltip label="Recalculate zone stats" hasArrow>
                          <IconButton
                            icon={<Icon as={MdRefresh} />}
                            size="xs"
                            colorScheme="purple"
                            variant="outline"
                            borderRadius="md"
                            isLoading={isRecalculating}
                            onClick={handleRecalculateZone}
                            aria-label="Recalculate zone stats"
                          />
                        </Tooltip>
                        <IconButton
                          icon={<CloseIcon boxSize={2.5} />}
                          size="xs"
                          variant="ghost"
                          onClick={() => setSelectedZone(null)}
                          aria-label="Close zone details"
                        />
                      </HStack>
                    </Flex>

                    {selectedZone.description && (
                      <Text fontSize="xs" color={mutedColor} mb={4} noOfLines={2}>
                        {selectedZone.description}
                      </Text>
                    )}

                    {/* Key metrics */}
                    <SimpleGrid columns={2} spacing={2} mb={4}>
                      <Box bg={statBg} p={2} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="2xs" color={mutedColor} textTransform="uppercase" fontWeight="700" mb={0.5}>
                          Total Assets
                        </Text>
                        <Text fontSize="lg" fontWeight="800" color="blue.500" lineHeight="1">
                          {selectedZone.total_assets ?? 0}
                        </Text>
                      </Box>
                      <Box bg={statBg} p={2} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                        <Text fontSize="2xs" color={mutedColor} textTransform="uppercase" fontWeight="700" mb={0.5}>
                          Avg Health
                        </Text>
                        <Text
                          fontSize="lg"
                          fontWeight="800"
                          lineHeight="1"
                          color={getHealthColor(selectedZone.average_health_score)}
                        >
                          {Number(selectedZone.average_health_score ?? 100).toFixed(1)}%
                        </Text>
                      </Box>
                      {/* Active WO spans both columns */}
                      <Box
                        bg={statBg}
                        p={2}
                        borderRadius="lg"
                        borderWidth="1px"
                        borderColor={borderColor}
                        gridColumn="span 2"
                      >
                        <Text fontSize="2xs" color={mutedColor} textTransform="uppercase" fontWeight="700" mb={0.5}>
                          Active Work Orders
                        </Text>
                        <Text fontSize="xl" fontWeight="800" color="orange.400" lineHeight="1">
                          {selectedZone.active_work_orders ?? 0}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    {/* Asset breakdown */}
                    {selectedZone.asset_breakdown &&
                      Object.keys(selectedZone.asset_breakdown).length > 0 && (
                        <>
                          <Divider mb={3} />
                          <Text fontSize="xs" fontWeight="700" mb={2} color={headingColor}>
                            Asset Breakdown
                          </Text>
                          <Wrap spacing={1.5}>
                            {Object.entries(selectedZone.asset_breakdown).map(([type, count]) => (
                              <WrapItem key={type}>
                                <Badge
                                  colorScheme="gray"
                                  textTransform="capitalize"
                                  borderRadius="full"
                                  px={2}
                                  fontSize="2xs"
                                >
                                  {type}: {count}
                                </Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </>
                      )}
                  </GlassPanel>
                </MotionBox>
              )}
            </AnimatePresence>

            {/* Bottom-left: Legend overlay — switches based on AI mode */}
            <Box position="absolute" bottom={4} left={4} zIndex={400} display={{ base: 'none', md: 'block' }}>
              <GlassPanel px={3} py={2}>
                <Text fontSize="2xs" fontWeight="700" color={mutedColor} textTransform="uppercase" mb={1.5} letterSpacing="wider">
                  {aiMode ? 'Health Legend' : 'Asset Legend'}
                </Text>
                {/* --- UPDATE: live + ML overlays */}
                <HStack spacing={3} mb={1.5}>
                  <HStack spacing={1.5}>
                    <Box w="10px" h="10px" borderRadius="full" borderWidth="2px" borderColor="#E53E3E" bg="rgba(229,62,62,0.25)" />
                    <Text fontSize="2xs" color={mutedColor}>Live anomaly</Text>
                  </HStack>
                  <HStack spacing={1.5}>
                    <Box w="10px" h="10px" borderRadius="full" borderWidth="2px" borderStyle="dashed" borderColor="#805AD5" />
                    <Text fontSize="2xs" color={mutedColor}>ML risk ≥ 70%</Text>
                  </HStack>
                </HStack>
                {/* --- END UPDATE */}
                <SimpleGrid columns={2} spacing={1.5}>
                  {aiMode ? (
                    <>
                      <HStack spacing={1.5}>
                        <Box w="10px" h="10px" borderRadius="full" bg={AI_HEALTH_COLORS.optimal} />
                        <Text fontSize="2xs" color={mutedColor}>Optimal 80%+</Text>
                      </HStack>
                      <HStack spacing={1.5}>
                        <Box w="10px" h="10px" borderRadius="full" bg={AI_HEALTH_COLORS.warning} />
                        <Text fontSize="2xs" color={mutedColor}>Deteriorating</Text>
                      </HStack>
                      <HStack spacing={1.5}>
                        <Box w="10px" h="10px" borderRadius="full" bg={AI_HEALTH_COLORS.critical} />
                        <Text fontSize="2xs" color={mutedColor}>Critical Risk</Text>
                      </HStack>
                    </>
                  ) : (
                    Object.entries(ASSET_COLORS)
                      .filter(([key]) => key !== 'default')
                      .map(([key, color]) => (
                        <HStack key={key} spacing={1.5}>
                          <Box w="10px" h="10px" borderRadius="full" bg={color} />
                          <Text fontSize="2xs" textTransform="capitalize" color={mutedColor}>{key}</Text>
                        </HStack>
                      ))
                  )}
                </SimpleGrid>
              </GlassPanel>
            </Box>
          </Box>
        </Flex>
      </MotionBox>

      {/* ================================================================== */}
      {/* PHASE 4: ASSET COMMAND DRAWER — The Action Hub                     */}
      {/* ================================================================== */}
      <Drawer isOpen={isDetailOpen} placement="right" onClose={onDetailClose} size="md">
        <DrawerOverlay backdropFilter="blur(4px)" />
        <DrawerContent bg={cardBg} borderLeftWidth="1px" borderColor={borderColor}>
          <DrawerCloseButton mt={1} />

          <DrawerHeader borderBottomWidth="1px" borderColor={borderColor} pb={4}>
            {selectedAsset && (
              <HStack spacing={3}>
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="lg"
                  bg={getDynamicColor(selectedAsset)}
                  align="center"
                  justify="center"
                  flexShrink={0}
                >
                  <Icon as={getAssetReactIcon(selectedAsset.type)} color="white" boxSize={4} />
                </Flex>
                <VStack align="start" spacing={0.5}>
                  <Text fontSize="lg" fontWeight="bold" color={headingColor} lineHeight="1.2">
                    {selectedAsset.name}
                  </Text>
                  <HStack spacing={2}>
                    <Badge
                      colorScheme={getStatusConfig(selectedAsset.status).color}
                      borderRadius="md"
                      fontSize="xs"
                      px={2}
                    >
                      {getStatusConfig(selectedAsset.status).label}
                    </Badge>
                    <Text fontSize="xs" color={mutedColor}>
                      {selectedAsset.type || 'Asset'} • ID: {selectedAsset.id}
                    </Text>
                  </HStack>
                </VStack>
              </HStack>
            )}
          </DrawerHeader>

          <DrawerBody p={0} overflowY="auto">
            {selectedAsset && (
              <VStack spacing={0} align="stretch">

                {/* -------------------------------------------------------- */}
                {/* 1. AI DIAGNOSTICS                                        */}
                {/* -------------------------------------------------------- */}
                <Box
                  p={6}
                  bg={aiMode ? aiSectionBg : drawerSectionBg} // --- UPDATE: dark-mode safe
                  borderBottomWidth="1px"
                  borderColor={borderColor}
                >
                  <HStack mb={4} spacing={2}>
                    <Icon
                      as={FaBrain}
                      color={aiMode ? 'purple.500' : mutedColor}
                      boxSize={3.5}
                    />
                    <Heading
                      size="xs"
                      textTransform="uppercase"
                      color={aiMode ? 'purple.600' : mutedColor}
                      letterSpacing="wider"
                    >
                      AI Diagnostics
                    </Heading>
                    {aiMode && (
                      <Badge colorScheme="purple" fontSize="2xs" variant="subtle" ml="auto">
                        AI Lens Active
                      </Badge>
                    )}
                  </HStack>

                  <SimpleGrid columns={2} spacing={4} mb={4}>
                    <Stat bg={cardBg} p={3} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
                      <StatLabel fontSize="xs" color={mutedColor}>Health Score</StatLabel>
                      <StatNumber
                        fontSize="2xl"
                        color={getHealthColor(selectedAsset.health_score)}
                        fontWeight="800"
                      >
                        {selectedAsset.health_score ?? 100}%
                      </StatNumber>
                    </Stat>
                    <Stat bg={cardBg} p={3} borderRadius="xl" boxShadow="sm" borderWidth="1px" borderColor={borderColor}>
                      <StatLabel fontSize="xs" color={mutedColor}>Est. Failure</StatLabel>
                      <StatNumber fontSize="lg" mt={1} color={headingColor} fontWeight="700">
                        {selectedAsset.remaining_useful_life != null
                          ? `${selectedAsset.remaining_useful_life}d`
                          : '>30d'}
                      </StatNumber>
                    </Stat>
                  </SimpleGrid>

                  {/* Health progress bar */}
                  <Box>
                    <Flex justify="space-between" mb={1.5}>
                      <Text fontSize="xs" color={mutedColor} fontWeight="600">
                        Condition: {getHealthLabel(selectedAsset.health_score)}
                      </Text>
                      <Text fontSize="xs" color={getHealthColor(selectedAsset.health_score)} fontWeight="700">
                        {selectedAsset.health_score ?? 100}%
                      </Text>
                    </Flex>
                    <Progress
                      value={selectedAsset.health_score ?? 100}
                      max={100}
                      size="sm"
                      colorScheme={getHealthColorScheme(selectedAsset.health_score)}
                      borderRadius="full"
                      bg={aiMode ? 'purple.100' : 'gray.200'}
                    />
                  </Box>

                  {/* Predicted failure date row */}
                  {selectedAsset.predicted_failure_date && (
                    <HStack
                      mt={3}
                      p={2.5}
                      bg={cardBg}
                      borderRadius="lg"
                      borderWidth="1px"
                      borderColor={borderColor}
                      spacing={2}
                    >
                      <Icon as={FaExclamationTriangle} color="orange.400" boxSize={3.5} />
                      <VStack spacing={0} align="start">
                        <Text fontSize="2xs" color={mutedColor} fontWeight="600">Predicted Failure Date</Text>
                        <Text fontSize="xs" fontWeight="700" color={headingColor}>
                          {new Date(selectedAsset.predicted_failure_date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </Text>
                      </VStack>
                    </HStack>
                  )}
                </Box>

                {/* --- UPDATE: FlowSense AI insight (priority, ML model, Ask Ollama) --- */}
                <AssetAiInsight asset={selectedEnriched} liveAlert={liveAlerts[String(selectedAsset.id)]} brief={gridBrief} />
                {/* --- END UPDATE --- */}

                {/* -------------------------------------------------------- */}
                {/* 2. LIVE TELEMETRY — Smart conditional rendering.          */}
                {/* hasTelemetry === true  → real AssetTelemetryChart        */}
                {/* hasTelemetry === false → friendly geospatial placeholder  */}
                {/* -------------------------------------------------------- */}
                {hasTelemetry ? (
                  <Box p={6} borderBottomWidth="1px" borderColor={borderColor}>
                    <HStack justify="space-between" mb={4}>
                      <HStack spacing={2}>
                        <Icon as={FaChartLine} color={mutedColor} boxSize={3.5} />
                        <Heading size="xs" textTransform="uppercase" color={mutedColor} letterSpacing="wider">
                          Pressure Trend (Last 24 Readings)
                        </Heading>
                      </HStack>
                      <HStack spacing={2}>
                        {isLoadingHistory && <Spinner size="xs" color="blue.500" />}
                        {selectedAsset.last_psi != null && (
                          <Badge
                            colorScheme="blue"
                            variant="subtle"
                            fontSize="sm"
                            px={3}
                            py={1}
                            borderRadius="lg"
                            fontWeight="700"
                          >
                            {selectedAsset.last_psi} PSI
                          </Badge>
                        )}
                      </HStack>
                    </HStack>

                    <Box
                      p={2}
                      bg={statBg}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderColor={borderColor}
                      minH="160px"
                    >
                      {assetHistory.length > 0 ? (
                        <AssetTelemetryChart
                          data={assetHistory}
                          color={getDynamicColor(selectedAsset)}
                        />
                      ) : (
                        <Center h="140px">
                          <VStack spacing={2}>
                            <Icon as={MdSensors} color="gray.400" boxSize={6} />
                            <Text fontSize="xs" color="gray.500" fontWeight="500">
                              {isLoadingHistory
                                ? 'Loading telemetry data...'
                                : 'No recent telemetry data available'}
                            </Text>
                          </VStack>
                        </Center>
                      )}
                    </Box>

                    {/* Quick metadata stats row below the chart */}
                    <SimpleGrid columns={3} spacing={3} mt={4}>
                      {[
                        { label: 'Zone', value: selectedAsset.zone_name || '—' },
                        {
                          label: 'Installed',
                          value: selectedAsset.installation_date
                            ? new Date(selectedAsset.installation_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                            : '—',
                        },
                        {
                          label: 'Last Service',
                          value: selectedAsset.last_maintenance_date
                            ? new Date(selectedAsset.last_maintenance_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                            : '—',
                        },
                      ].map(({ label, value }) => (
                        <Box key={label} bg={statBg} p={2.5} borderRadius="lg" textAlign="center">
                          <Text fontSize="2xs" color={mutedColor} fontWeight="600" textTransform="uppercase" mb={0.5}>
                            {label}
                          </Text>
                          <Text fontSize="xs" fontWeight="700" color={headingColor} noOfLines={1}>
                            {value}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                ) : (
                  /* ── Geospatial-only asset: no live telemetry ── */
                  <Box
                    p={6}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                    bg={geoPlaceholderBg}
                  >
                    <HStack spacing={2} mb={3}>
                      <Icon as={FaChartLine} color={mutedColor} boxSize={3.5} />
                      <Heading size="xs" textTransform="uppercase" color={mutedColor} letterSpacing="wider">
                        Telemetry
                      </Heading>
                    </HStack>

                    <Center py={5}>
                      <VStack spacing={2}>
                        <Flex
                          w="44px"
                          h="44px"
                          borderRadius="xl"
                          bg={geoIconBg}
                          align="center"
                          justify="center"
                        >
                          <Icon as={MdGpsFixed} color="gray.400" boxSize={5} />
                        </Flex>
                        <Text fontSize="sm" fontWeight="600" color={mutedColor}>
                          Geospatial Asset
                        </Text>
                        <Text fontSize="xs" color={mutedColor} textAlign="center" maxW="220px">
                          No live telemetry sensors attached to this asset. Location and status data only.
                        </Text>
                      </VStack>
                    </Center>

                    {/* Still surface the key metadata stats even without telemetry */}
                    <SimpleGrid columns={3} spacing={3} mt={2}>
                      {[
                        { label: 'Zone', value: selectedAsset.zone_name || '—' },
                        {
                          label: 'Installed',
                          value: selectedAsset.installation_date
                            ? new Date(selectedAsset.installation_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                            : '—',
                        },
                        {
                          label: 'Last Service',
                          value: selectedAsset.last_maintenance_date
                            ? new Date(selectedAsset.last_maintenance_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
                            : '—',
                        },
                      ].map(({ label, value }) => (
                        <Box key={label} bg={cardBg} p={2.5} borderRadius="lg" textAlign="center" borderWidth="1px" borderColor={borderColor}>
                          <Text fontSize="2xs" color={mutedColor} fontWeight="600" textTransform="uppercase" mb={0.5}>
                            {label}
                          </Text>
                          <Text fontSize="xs" fontWeight="700" color={headingColor} noOfLines={1}>
                            {value}
                          </Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Box>
                )}

                {/* -------------------------------------------------------- */}
                {/* 3. SMART ACTION HUB                                       */}
                {/* -------------------------------------------------------- */}
                <Box p={6}>
                  <HStack mb={4} spacing={2}>
                    <Icon as={FaWrench} color={mutedColor} boxSize={3.5} />
                    <Heading size="xs" textTransform="uppercase" color={mutedColor} letterSpacing="wider">
                      Quick Actions
                    </Heading>
                  </HStack>

                  <VStack spacing={3} w="100%">

                    {/* ── CURRENT WORK ORDER STATUS BLOCK ──
                         Shows the active ticket if one exists, or a dashed
                         placeholder if no ticket is currently open for this asset.
                         This block sits above the Generate button so the admin
                         can see the WO status at a glance before taking action. */}
                    {activeWorkOrder ? (
                      <Box
                        w="100%"
                        p={3}
                        bg={activeWoBg}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={activeWoBorder}
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          color={activeWoText}
                          textTransform="uppercase"
                          mb={1}
                        >
                          Current Work Order
                        </Text>
                        <Text fontSize="sm" fontWeight="600" color={activeWoTitle} noOfLines={1}>
                          {activeWorkOrder.title || `Ticket #${activeWorkOrder.id}`}
                        </Text>
                        <HStack mt={2} justify="space-between">
                          <Badge colorScheme="blue" textTransform="capitalize">
                            {activeWorkOrder.status?.replace('_', ' ')}
                          </Badge>
                          <Button
                            size="xs"
                            variant="link"
                            colorScheme="blue"
                            onClick={() => {
                              // --- UPDATE: WorkOrderPage deep-links with ?search=<id> (opens the job)
                              onDetailClose();
                              navigate(`/admin/work-orders?search=${encodeURIComponent(activeWorkOrder.id)}`);
                            }}
                          >
                            View Details
                          </Button>
                        </HStack>
                      </Box>
                    ) : (
                      <Box
                        w="100%"
                        p={3}
                        bg={statBg}
                        borderRadius="xl"
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderStyle="dashed"
                      >
                        <Text fontSize="sm" color={mutedColor} textAlign="center" fontWeight="500">
                          No current workorder for this asset
                        </Text>
                      </Box>
                    )}

                    {/* ── Generate Work Order button — locked when WO exists ── */}
                    <Tooltip
                      label={
                        activeWorkOrder
                          ? `Ticket #${activeWorkOrder.id || 'Active'} is already handling this asset.`
                          : selectedAsset?.status === 'maintenance'
                          ? 'A work order is already active for this asset.'
                          : ''
                      }
                      hasArrow
                      placement="top"
                      isDisabled={!activeWorkOrder && selectedAsset?.status !== 'maintenance'}
                    >
                      <Box w="100%">
                        <Button
                          leftIcon={
                            <Icon
                              as={
                                activeWorkOrder || selectedAsset?.status === 'maintenance'
                                  ? FaCheckCircle
                                  : FaWrench
                              }
                            />
                          }
                          colorScheme={
                            activeWorkOrder || selectedAsset?.status === 'maintenance'
                              ? 'green'
                              : 'blue'
                          }
                          w="100%"
                          size="md"
                          borderRadius="xl"
                          fontWeight="700"
                          boxShadow="sm"
                          isLoading={isGeneratingTriage}
                          loadingText="Drafting Ticket..."
                          onClick={handleGenerateWorkOrder}
                          isDisabled={
                            !!activeWorkOrder || selectedAsset?.status === 'maintenance'
                          }
                        >
                          {activeWorkOrder || selectedAsset?.status === 'maintenance'
                            ? 'Work Order Active'
                            : 'Generate Work Order'}
                        </Button>
                      </Box>
                    </Tooltip>

                    {/* --- UPDATE: review the typed draft in the work order form instead --- */}
                    {!activeWorkOrder && selectedAsset?.status !== 'maintenance' && (
                      <Button
                        leftIcon={<Icon as={MdOpenInNew} />}
                        variant="ghost"
                        colorScheme="blue"
                        w="100%"
                        size="sm"
                        borderRadius="xl"
                        onClick={handleOpenInForm}
                      >
                        Open in work order form
                      </Button>
                    )}
                    {/* --- END UPDATE --- */}

                    {/* ── Ollama AI Triage ── */}
                    <Button
                      leftIcon={<Icon as={FaBrain} />}
                      colorScheme="purple"
                      variant="outline"
                      w="100%"
                      size="md"
                      borderRadius="xl"
                      fontWeight="700"
                      isLoading={isGeneratingTriage}
                      loadingText="Running Triage..."
                      onClick={handleOllamaTriage}
                    >
                      Run AI Triage Brief
                    </Button>

                    {/* ── Secondary actions ── */}
                    <Button
                      leftIcon={<Icon as={FaHistory} />}
                      variant="outline"
                      w="100%"
                      size="md"
                      borderRadius="xl"
                      fontWeight="600"
                      onClick={onHistoryOpen}
                    >
                      View Maintenance Log
                    </Button>

                    <Button
                      leftIcon={<SettingsIcon />}
                      variant="ghost"
                      w="100%"
                      size="md"
                      borderRadius="xl"
                      fontWeight="600"
                      color={mutedColor}
                      onClick={() =>
                        toast({
                          title: 'Sensor Configuration',
                          description: `Opening threshold settings for ${selectedAsset.name}`,
                          status: 'info',
                          duration: 2000,
                          isClosable: true,
                        })
                      }
                    >
                      Configure Sensor Thresholds
                    </Button>
                  </VStack>

                  {/* Asset coordinates footer */}
                  <Box mt={5} pt={4} borderTopWidth="1px" borderColor={borderColor}>
                    <Text fontSize="2xs" color={mutedColor} textAlign="center">
                      {selectedAsset.latitude.toFixed(6)}, {selectedAsset.longitude.toFixed(6)}
                    </Text>
                  </Box>
                </Box>

              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* ================================================================== */}
      {/* MAINTENANCE HISTORY MODAL                                         */}
      {/* ================================================================== */}
      <Modal
        isOpen={isHistoryOpen}
        onClose={onHistoryClose}
        size="6xl"
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay backdropFilter="blur(10px)" bg="blackAlpha.700" />
        <ModalContent bg={cardBg} borderRadius="2xl" overflow="hidden" h="85vh">
          <ModalHeader borderBottomWidth="1px" borderColor={borderColor}>
            <HStack spacing={3}>
              <Flex
                align="center"
                justify="center"
                w={10}
                h={10}
                borderRadius="xl"
                bg="purple.50"
              >
                <Icon as={FaHistory} color="purple.500" boxSize={5} />
              </Flex>
              <Box>
                <Heading size="md">Maintenance Ledger</Heading>
                <Text fontSize="sm" color={mutedColor}>
                  {selectedAsset?.name} ({selectedAsset?.id})
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton mt={2} />

          <ModalBody p={6} bg={modalBodyBg}>
            {/* Suspense boundary for the lazy-loaded MaintenanceHistory module */}
            <React.Suspense
              fallback={
                <Flex h="200px" align="center" justify="center" direction="column" gap={3}>
                  <Spinner size="lg" color="purple.400" thickness="3px" />
                  <Text fontSize="sm" color={mutedColor}>Loading maintenance ledger...</Text>
                </Flex>
              }
            >
              {selectedAsset && (
                <MaintenanceHistory
                  assetId={selectedAsset.id}
                  enableFilters={true}
                  enableStatistics={true}
                  enableAdvancedAnalytics={true}
                  enableRealTimeUpdates={false}
                />
              )}
            </React.Suspense>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* --- UPDATE: Grid AI drawer + FlowSense guide --- */}
      <GridAiPanel
        isOpen={isGridAiOpen}
        onClose={onGridAiClose}
        brief={gridBrief}
        assets={enrichedAssets}
        selectedAsset={selectedEnriched}
        onFocusAsset={handleFocusAsset}
        aiStatus={aiStatus}
        wsConnected={isConnected}
        feed={feed}
        systemBrief={systemBrief}
        onOpenGuide={onGuideOpen}
      />
      <DashboardAiHelper isOpen={isGuideOpen} onClose={onGuideClose} onOpenAi={onGridAiOpen} />
      {/* --- END UPDATE --- */}

      {/* ================================================================== */}
      {/* CUSTOM CSS                                                        */}
      {/* ================================================================== */}
      <style>
        {`
          @keyframes telemetry-pulse {
            0% { transform: scale(1); opacity: 0.6; }
            100% { transform: scale(2.5); opacity: 0; }
          }
          .telemetry-pulse {
            animation: telemetry-pulse 1s ease-out infinite;
            transform-origin: center;
          }
          /* --- UPDATE: live anomaly pulse */
          @keyframes live-alert-pulse {
            0% { stroke-opacity: 1; fill-opacity: 0.35; }
            50% { stroke-opacity: 0.3; fill-opacity: 0.08; }
            100% { stroke-opacity: 1; fill-opacity: 0.35; }
          }
          .live-alert-pulse {
            animation: live-alert-pulse 1.2s ease-in-out infinite;
          }
          .custom-svg-icon {
            background: none !important;
            border: none !important;
          }
          .custom-asset-popup .leaflet-popup-content-wrapper {
            border-radius: 12px;
            box-shadow: 0 8px 32px rgba(0,0,0,0.15);
            padding: 4px;
          }
          .custom-asset-popup .leaflet-popup-tip {
            box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          }
          .leaflet-container {
            font-family: inherit;
          }
          .custom-zone-tooltip {
            background: rgba(255, 255, 255, 0.96);
            border: 1px solid #D6BCFA;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.10);
            padding: 6px 10px;
          }
        `}
      </style>
    </Box>
  );
}